import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react"
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Box,
  Crosshair,
  Eye,
  Group,
  Loader2,
  MonitorSmartphone,
  Moon,
  Palette,
  Play,
  Rotate3d,
  Scaling,
  Shapes,
  SkipBack,
  Square,
  Sun,
  Trash2,
  Ungroup,
  Video,
  X,
} from "lucide-react"
import { toast } from "sonner"
import { ConfirmActionDialog } from "@/components/confirm-action-dialog"
import { ObjectTrajectoryIcon } from "@/components/icons/object-trajectory-icon"
import { SketchDialog } from "@/components/sketch/sketch-dialog"
import { HeroFigurePicker } from "@/components/locations/hero-figure-picker"
import { SceneColorPicker } from "@/components/locations/scene-color-picker"
import {
  DeviceFlyStick,
  DEVICE_FLY_STICK_BOTTOM_CLASS,
  DEVICE_FLY_STICK_DEADZONE_PX,
  DEVICE_FLY_STICK_MODAL_BOTTOM_CLASS,
  DEVICE_FLY_STICK_RIGHT_CLASS,
  DEVICE_FLY_STICK_SIZE_PX,
} from "@/components/locations/device-fly-stick"
import { CaptureControls, type CapturePhase } from "@/components/locations/capture-controls"
import { LocationScenePathEditPanel } from "@/components/locations/location-scene-path-edit-panel"
import { LocationScenePathTimeline } from "@/components/locations/location-scene-path-timeline"
import { MOVE_KEY_AXIS, MoveKeys, type MoveKeyCode } from "@/components/locations/move-keys"
import { LocationScene3dToolbar } from "@/components/locations/location-scene-3d-toolbar"
import {
  VIEW_CUBE_CORNER_CLASS,
  ViewCubeWidget,
  type ViewCubeWidgetHandle,
} from "@/components/locations/view-cube-widget"
import { StageAnchored } from "@/components/locations/stage-anchored"
import {
  LocationScenePlatformDialog,
  type LightPresetsApi,
  type PlatformFormApi,
  type SelectionExtrudeApi,
} from "@/components/locations/location-scene-platform-dialog"
import type { CastRow, PropRow } from "@/components/prompt-editor/mention-sources"
import { ReferencePickerDrawer } from "@/components/references/reference-picker-drawer"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { Button } from "@/components/ui/primitives"
import { EditToolbarButton } from "@/components/ui/edit-toolbar-button"
import { UiRange } from "@/components/ui/ui-range"
import { useLocationSceneFacePoll } from "@/hooks/use-location-scene-face-poll"
import { useLocationSceneHistory } from "@/hooks/use-location-scene-history"
import { useLocationScenePersist, type TranslateFn } from "@/hooks/use-location-scene-persist"
import { useTranslation } from "@/hooks/use-translation"
import { snapshotFromVisual } from "@/lib/location-scene-3d/action-history"
import { characterFigureOrHumanoid, type SceneCharacterFigure } from "@/lib/location-scene-3d/character-figure"
import { createCameraLiveSync, type CameraLiveSync } from "@/lib/location-scene-3d/camera-live-sync"
import {
  createCameraLivePoseSmoother,
  type CameraLivePoseSmoother,
} from "@/lib/location-scene-3d/camera-live-pose-smooth"
import {
  cameraPathDurationMs,
  cameraPathsEqual,
  canLookFromCamera,
  cloneCameraPath,
  emptyCameraPath,
  hasCameraPath,
  pathEditHistoryLabelKey,
  type CameraPath,
  type PathEditUi,
} from "@/lib/location-scene-3d/camera-path"
import {
  cameraPlaceAimFromStart,
  cameraPlaceMonitorVisible,
  cameraPlaceTakeIsEmpty,
} from "@/lib/location-scene-3d/camera-place"
import {
  cameraPathTakesFull,
  nextCameraPathTakeNumber,
  openingCameraPathTake,
} from "@/lib/location-scene-3d/camera-path-takes"
import { DEFAULT_SPLINE_DURATION_MS } from "@/lib/location-scene-3d/camera-path-spline"
import {
  cloneObjectPath,
  objectPathHasContent,
  sceneClockMs,
  sceneHasMotion,
  type ObjectPath,
} from "@/lib/location-scene-3d/object-path"
import { savePrevizFromEngine } from "@/lib/location-scene-3d/save-previz-from-engine"
import {
  requestDeviceOrientationPermission,
  startDeviceOrientationLook,
  type DeviceLookMode,
  type DeviceOrientationLookHandle,
} from "@/lib/location-scene-3d/device-orientation-look"
import {
  createScene3dEngine,
  type PlatformSelectionToolbarAnchorScreen,
  type Scene3dEngine,
} from "@/lib/location-scene-3d/engine"
import {
  buildCharacterObject,
  buildPrimitiveObject,
  buildPropObject,
  defaultPrevizLayout,
  defaultSceneEnvironment,
  defaultSceneLight,
  emptySceneLayout,
  ensurePrevizPlatform,
  normalizeSceneEnvironment,
  normalizeSceneLayout,
  normalizeSceneLight,
  occupiedColorsForEntity,
  sceneStateForSave,
  type SceneCameraState,
  type SceneGizmoMode,
  type SceneLayoutState,
  type SceneLightState,
  type SceneObjectState,
  type ScenePrimitiveShape,
  type SceneThemeId,
} from "@/lib/location-scene-3d/scene-state"
import { paletteColorFor, PRIMITIVE_COLOR } from "@/lib/location-scene-3d/palette"
import { selectionPopoverAction } from "@/lib/location-scene-3d/object-groups"
import { characterListThumbnail } from "@/lib/character-thumbnail"
import { applyWallTileColors, emptyPlatform, platformIsSet, type ScenePlatform } from "@/lib/location-scene-3d/platform"
import type { PlatformLayerState, PlatformTool } from "@/lib/location-scene-3d/platform-layer"
import { frameFormatRatio, normalizeFrameFormat } from "@/lib/frame-format"
import { frameViewfinderRect, type FrameViewfinderRect } from "@/lib/location-scene-3d/previz-export-size"
import { SCENE_3D_STAGE_ATTR } from "@/lib/dialog-layer"
import { scene3dWorkspaceFlyUrl, scene3dWorkspaceViewUrl } from "@/lib/location-scene-hash"
import { fetchScene3d, renameScene3d, updateScene3dScene, type LibraryPrevizItem } from "@/lib/library-api"
import { remapSceneFaces } from "@/lib/location-scene-3d-api"
import type { CanvasReferenceItem } from "@/types/canvas"
import { cn } from "@/lib/utils"

export type Scene3dEditorFocus = {
  scene3d_id: number
  dirty: boolean
  opened_from: "gallery" | "shot"
  shot_id: number | null
  selection: {
    object_uid: string | null
    group_id?: string
    sectors: { x: number; z: number }[]
    face_tiles: { face_key: string; u: number; v: number }[]
  }
  scene_state: ReturnType<typeof sceneStateForSave>
}

export type LocationScene3dHandle = {
  requestReset: () => void
  /** True when camera path exists but has not been exported as previz video. */
  isPathExportDirty: () => boolean
  /** Export path video (same as timeline Save). Clears path dirty on success. */
  savePathPreviz: () => Promise<boolean>
  /** Replace the scene tile poster with the current view. */
  uploadPosterIfNeeded: () => Promise<boolean>
  /** Snapshot the scene and a small poster, then return. Save and upload continue after unmount. */
  schedulePosterUpload: () => Promise<boolean>
  /** Path video is recording. Poster snapshot would rewrite that frame. */
  isExporting: () => boolean
  /** Snapshot for the 3D assistant. Read at send time, not on every camera frame. */
  agentFocus: () => Scene3dEditorFocus | null
  /** Replace the open viewport with a server layout. Keeps the current selection when it still exists. */
  applyServerLayout: (raw: unknown) => void
  /** Select an object and ease the camera onto it. */
  focusObject: (uid: string) => void
  /** Select an object and open its path editor. Same uid again leaves the editor. */
  editObjectPath: (uid: string) => void
  /** Show one object path as a line. Same uid again hides it. */
  showObjectPath: (uid: string) => void
  /** Same as the camera control: flash, then save the current view. */
  captureViewpoint: () => void
  /** Open or close the reference picker from the assistant Add object button. */
  toggleObjectPicker: () => void
}

export type LocationScene3dMode = "newPreviz" | "editPreviz"

type LocationScene3dProps = {
  projectId: number
  locationId: number | null
  locationName: string
  cast: CastRow[]
  props: PropRow[]
  canEdit?: boolean
  onLayoutChanged?: () => void
  /** Project frame format — used for previz export / platform faces (not stage CSS). */
  frameFormat?: string | null
  /** Открыто из шота: сохранённый превиз сразу привязывается к нему. */
  shotId?: number | null
  /** Герои шота: после загрузки layout недостающие добавляются в сцену. */
  presetCharacterIds?: number[]
  onPrevizSaved?: (previz?: LibraryPrevizItem) => void
  /** Сохранить принято, до записи ролика. */
  onPrevizCreateStart?: () => void
  /** Upload превиза после закрытия редактора не удался. */
  onPrevizSaveFailed?: () => void
  /** Viewpoint still saved — refresh the gallery, do not close the editor. */
  onViewpointSaved?: (viewpoint?: { id: number }) => void
  /** Подсказки на кнопках съёмки. Только сразу после создания сцены. */
  captureHints?: boolean
  /** Save/export in progress — parent can lock modal close. */
  onExportingChange?: (exporting: boolean) => void
  /** Raise canvas portal above nested WorkspaceDialog (z 101/111/…). */
  nested?: boolean | 2 | 3
  /** Режим телефон-камеры: родитель прячет chrome диалога. */
  onImmersiveChange?: (immersive: boolean) => void
  /** Layout exists on server — for Clear in shell / panel header. */
  onExistsChange?: (exists: boolean) => void
  /** Deep-link / QR: enter phone-fly after modal + stage settle. */
  autoStartDeviceCamera?: boolean
  /** QR вида из камеры: та же модалка, гироскоп крутит камеру сцены. */
  autoStartCameraView?: boolean
  /** Fired once when phone-fly UI actually starts (strip the /fly or /view path). */
  onPhoneFlyEnter?: () => void
  /** User left the phone camera modal. */
  onDeviceCameraExit?: () => void
  /** Phone toolbar close (modal without header). */
  onRequestClose?: () => void
  /**
   * newPreviz — черновик превиза; editPreviz — правка существующего (PATCH scene).
   */
  sceneMode?: LocationScene3dMode
  /** Layout owner Scene3d id (autosave / faces / Photo / QR). */
  scene3dId?: number | null
  /** Video Previz id when editing an existing flythrough (Save video / poster). */
  previzId?: number | null
  /** Для editPreviz: начальный scene_state с карточки. */
  initialSceneState?: Partial<SceneLayoutState> | null
  /** Known video on open — path export dirty until a flythrough exists. */
  initialHasVideo?: boolean
  initialHasPoster?: boolean
  modelLabel?: string | null
  sceneName: string
  onSceneNameSaved?: (name: string) => void
  /** Панель шота: после записи превиза закрыть редактор, не показывая сцену. */
  dismissAfterPrevizSave?: boolean
  onDismissAfterPrevizCapture?: () => void
  /** Live objects and viewport selection for the scene object list. */
  onObjectCatalogChange?: (objects: SceneObjectState[], selectedUid: string | null) => void
  /** Path edit and path preview ids, only when those ids change. */
  onObjectPathModeChange?: (mode: { editUid: string | null; previewUid: string | null }) => void
  /** Viewpoint still is being saved. The side-panel button stays disabled. */
  onViewpointCaptureBusyChange?: (busy: boolean) => void
  /** Clicks on these nodes must not close the reference picker. */
  pickerIgnoreRefs?: RefObject<HTMLElement | null>[]
  /** Assistant «Add object» button. The picker opens upward from it. */
  assistantPickerAnchorRef?: RefObject<HTMLButtonElement | null>
}

/** Телефон/тач: кнопка «телефон-камера» (не десктоп с мышью). */
const DEVICE_CAMERA_MQ = "(pointer: coarse), (max-width: 767px)"

function readPhoneLayout(): boolean {
  if (typeof window === "undefined") return false
  return window.matchMedia(DEVICE_CAMERA_MQ).matches
}
/** Дефолт скорости полёта в телефон-режиме (в 2 раза ниже десктопного 4.5). */
const DEVICE_FLY_DEFAULT_SPEED = 2.25
const DEVICE_FLY_SPEED_PER_PX = 0.008
/** Yaw rate grows with |dx| like fly speed with |dy|; cap when pulled far. */
const DEVICE_FLY_YAW_PER_PX = 0.0075
const DEVICE_FLY_MAX_YAW_RATE = 1.1
const DEVICE_FLY_DOUBLE_TAP_MS = 320
/** Ignore double-tap exit right after enter (ghost pointer from drone tap). */
const DEVICE_FLY_ENTER_GRACE_MS = 500
/** Fly chrome above stage (200) and agent / shell chrome. */
const DEVICE_FLY_STAGE_Z = 200

/** Wall face media commits are not undoable — blobs live on the server. */
const FACE_MEDIA_HISTORY_LABEL_KEYS = new Set([
  "locationsPage.scene3d.historyActions.faceUpload",
  "locationsPage.scene3d.historyActions.faceGenerate",
  "locationsPage.scene3d.historyActions.faceLibrary",
  "locationsPage.scene3d.historyActions.faceClear",
])

/** Square video record control shared by phone-fly and phone modal chrome. */
function PhoneFlyRecordButton({
  bottomClass,
  recording,
  recordArmed,
  label,
  onToggle,
  className,
}: {
  bottomClass: string
  recording: boolean
  recordArmed: boolean
  label: string
  onToggle: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      className={cn(
        "pointer-events-auto absolute z-50 flex items-center justify-center rounded-lg border border-white/25 bg-black/45 text-white/90 shadow-lg",
        "touch-none backdrop-blur-sm outline-none select-none [-webkit-touch-callout:none]",
        "focus:outline-none focus-visible:ring-0 focus-visible:outline-none",
        "disabled:pointer-events-none disabled:opacity-60",
        bottomClass,
        DEVICE_FLY_STICK_RIGHT_CLASS,
        (recording || recordArmed) && "border-red-400/50 bg-red-600/70 text-white",
        recordArmed && !recording && "animate-pulse",
        className,
      )}
      style={{ width: DEVICE_FLY_STICK_SIZE_PX, height: DEVICE_FLY_STICK_SIZE_PX }}
      title={label}
      aria-label={label}
      aria-pressed={recording || recordArmed}
      {...{ "data-device-fly-record": "" }}
      onContextMenu={(e) => e.preventDefault()}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={onToggle}
    >
      <Video className="h-7 w-7" strokeWidth={1} aria-hidden />
    </button>
  )
}

function readDeviceFlyViewportBox(): { top: number; left: number; width: number; height: number } {
  const vv = typeof window !== "undefined" ? window.visualViewport : null
  if (vv && vv.width >= 32 && vv.height >= 32) {
    return {
      top: Math.round(vv.offsetTop),
      left: Math.round(vv.offsetLeft),
      width: Math.round(vv.width),
      height: Math.round(vv.height),
    }
  }
  return {
    top: 0,
    left: 0,
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  }
}

type FlyInputMerge = {
  forward: boolean
  backward: boolean
  left: boolean
  right: boolean
  up: boolean
  down: boolean
}

const EMPTY_FLY_MERGE: FlyInputMerge = {
  forward: false,
  backward: false,
  left: false,
  right: false,
  up: false,
  down: false,
}

const CAPTURE_HINT_MS = 3000

type CaptureHintStep = "snapshot" | "record" | "place"

function startCamerasEqual(a: SceneCameraState | null, b: SceneCameraState | null): boolean {
  return JSON.stringify(cloneStartCamera(a)) === JSON.stringify(cloneStartCamera(b))
}

function objectPathFingerprint(path: ObjectPath | null | undefined): string | null {
  if (!path) return null
  return JSON.stringify(cloneObjectPath(path))
}

function cloneStartCamera(cam: SceneCameraState | null): SceneCameraState | null {
  if (!cam) return null
  const copy: SceneCameraState = {
    pos: [cam.pos[0], cam.pos[1], cam.pos[2]],
    yaw: cam.yaw,
    pitch: cam.pitch,
    fov: cam.fov,
  }
  if (cam.target) copy.target = [cam.target[0], cam.target[1], cam.target[2]]
  if (cam.mode) copy.mode = cam.mode
  return copy
}

function captureHintStorageKey(scene3dId: number) {
  return `frameriver:scene3d-capture-hint:${scene3dId}`
}

function captureHintSeen(scene3dId: number) {
  try {
    return localStorage.getItem(captureHintStorageKey(scene3dId)) === "1"
  } catch {
    return false
  }
}

function markCaptureHintSeen(scene3dId: number) {
  try {
    localStorage.setItem(captureHintStorageKey(scene3dId), "1")
  } catch {
    // Приватный режим не должен блокировать съёмку.
  }
}

function observeObjectThumb(object: SceneObjectState | undefined, cast: CastRow[], props: PropRow[]): string | null {
  if (!object || object.kind === "primitive" || object.entity_id == null) return null
  if (object.kind === "character") {
    const character = cast.find((row) => row.id === object.entity_id)
    return character ? characterListThumbnail(character) : null
  }
  const prop = props.find((row) => row.id === object.entity_id)
  const front = prop?.front_view_thumb_url?.trim()
  if (front) return front
  const thumb = prop?.thumb_url?.trim()
  return thumb || null
}

function objectPopoverButtonClass(on: boolean) {
  return cn("h-8 w-8 text-white/85 hover:bg-white/20 hover:text-white", on && "bg-white/20 text-white ring-white/40")
}

export const LocationScene3d = forwardRef<LocationScene3dHandle, LocationScene3dProps>(function LocationScene3d(
  {
    projectId,
    locationId,
    locationName,
    cast,
    props,
    canEdit = true,
    onLayoutChanged,
    frameFormat,
    shotId,
    presetCharacterIds,
    onPrevizSaved,
    onPrevizCreateStart,
    onPrevizSaveFailed,
    onViewpointSaved,
    captureHints = false,
    onExportingChange,
    nested = false,
    onImmersiveChange,
    onExistsChange,
    autoStartDeviceCamera = false,
    autoStartCameraView = false,
    onPhoneFlyEnter,
    onDeviceCameraExit,
    onRequestClose,
    sceneMode = "newPreviz",
    scene3dId = null,
    previzId = null,
    initialSceneState = null,
    initialHasVideo = false,
    modelLabel = null,
    sceneName,
    onSceneNameSaved,
    dismissAfterPrevizSave = false,
    onDismissAfterPrevizCapture,
    onObjectCatalogChange,
    onObjectPathModeChange,
    onViewpointCaptureBusyChange,
    pickerIgnoreRefs,
    assistantPickerAnchorRef,
  },
  ref,
) {
  const { t } = useTranslation(["workspace", "common"])
  const frame = normalizeFrameFormat(frameFormat)
  const stageRef = useRef<HTMLDivElement>(null)
  const placeholderRef = useRef<HTMLDivElement>(null)
  const engineRef = useRef<Scene3dEngine | null>(null)
  const placeMonitorRef = useRef<HTMLCanvasElement | null>(null)
  const cameraPathTakeNameRef = useRef<(n: number) => string>((n) => `Take ${n}`)
  const cameraPathTakesFullRef = useRef<() => void>(() => {})
  cameraPathTakeNameRef.current = (n) => t("locationsPage.scene3d.timeline.takeName", { n })
  cameraPathTakesFullRef.current = () => toast.error(t("locationsPage.scene3d.timeline.takesFull"))
  const viewCubeRef = useRef<ViewCubeWidgetHandle>(null)
  const viewCubeDragRef = useRef(false)
  const platformInputRef = useRef({
    sectors: (_sectors: { x: number; z: number }[], _additive: boolean, _opts?: { prelude?: boolean }) => {},
    face: (_faceKey: string | null, _additive?: boolean) => {},
    faceTiles: (_tiles: { faceKey: string; u: number; v: number }[], _additive: boolean) => {},
    clear: () => {},
  })
  const platformVisualRef = useRef<PlatformLayerState | null>(null)
  const platformToolRef = useRef<PlatformTool>(null)
  const platformClearToolRef = useRef<() => void>(() => {})
  const platformActivateToolRef = useRef<(tool: "sector" | "surface") => void>(() => {})
  const selectionExtrudeRef = useRef<SelectionExtrudeApi | null>(null)
  const lightPresetsRef = useRef<LightPresetsApi | null>(null)
  const persistLightTimerRef = useRef<number | null>(null)
  const existsRef = useRef(false)
  const dirtyRef = useRef(false)
  const autosaveEnabledRef = useRef(sceneMode === "editPreviz" || scene3dId != null)
  const skipHistoryRef = useRef(false)
  const pathHistoryPrevRef = useRef<CameraPath | null>(null)
  const clearHistoryRedoRef = useRef<() => void>(() => {})
  const pathTimeListenerRef = useRef<(ms: number) => void>(() => {})
  const deviceLookRef = useRef<DeviceOrientationLookHandle | null>(null)
  const deviceLookModeRef = useRef<DeviceLookMode>("pitchOnly")
  const viewModalLookThroughRef = useRef(false)
  /** Проход взгляда в этой модалке уже был — закрытие не возвращает старую позу. */
  const phoneLookRewrittenRef = useRef(false)
  const flyInputMergeRef = useRef<FlyInputMerge>({ ...EMPTY_FLY_MERGE })
  const rightStickBaseSpeedRef = useRef(4.5)
  const lastStageTapRef = useRef(0)
  const startCameraRef = useRef<SceneCameraState | null>(null)
  /** Prior start captured in onCameraPlaced, consumed by the next path-change history step. */
  const placeStartUndoRef = useRef<{ prior: SceneCameraState | null } | null>(null)
  /** Pose before phone-fly; exit restores this if no start_camera. */
  const preDeviceCamRef = useRef<SceneCameraState | null>(null)
  const liveSyncRef = useRef<CameraLiveSync | null>(null)
  const livePoseSmootherRef = useRef<CameraLivePoseSmoother | null>(null)
  const followingActiveRef = useRef(false)
  const deviceCameraActiveRef = useRef(false)
  /** Bump on stop / new start so in-flight permission cannot re-attach gyro. */
  const deviceCamSessionRef = useRef(0)
  const followingBadgeTimerRef = useRef<number | null>(null)
  const addButtonRef = useRef<HTMLButtonElement>(null)
  const captureViewpointActionRef = useRef<() => void>(() => {})
  const toggleObjectPickerRef = useRef<() => void>(() => {})
  const flyEnterAtRef = useRef(0)
  const rememeasureStageBoxRef = useRef<() => void>(() => {})
  /** QR auto-enter: only once per autoStart cycle. */
  const flyAutoStartedRef = useRef(false)
  /** Gyro attached or permission already attempted this fly session. */
  const gyroReadyRef = useRef(false)
  const gyroAttachInFlightRef = useRef(false)
  const onPhoneFlyEnterRef = useRef(onPhoneFlyEnter)
  onPhoneFlyEnterRef.current = onPhoneFlyEnter
  const onDeviceCameraExitRef = useRef(onDeviceCameraExit)
  onDeviceCameraExitRef.current = onDeviceCameraExit
  const [boundScene3dId, setBoundScene3dId] = useState<number | null>(scene3dId)
  useEffect(() => {
    setBoundScene3dId(scene3dId)
  }, [scene3dId])
  const boundScene3dIdRef = useRef(boundScene3dId)
  boundScene3dIdRef.current = boundScene3dId
  /** Video previz id (separate from Scene3d layout id). */
  const [boundPrevizId, setBoundPrevizId] = useState<number | null>(previzId)
  useEffect(() => {
    setBoundPrevizId(previzId)
  }, [previzId])
  const boundPrevizIdRef = useRef(boundPrevizId)
  boundPrevizIdRef.current = boundPrevizId
  /** True after engine mount cleanup — block persist from teardown emits. */
  const disposedRef = useRef(false)

  const [objects, setObjects] = useState<SceneObjectState[]>([])
  const [selectedUids, setSelectedUids] = useState<string[]>([])
  const selectedUid = selectedUids.length === 1 ? selectedUids[0] : null
  const groupAction = useMemo(() => selectionPopoverAction(selectedUids, objects), [objects, selectedUids])
  const onObjectCatalogChangeRef = useRef(onObjectCatalogChange)
  onObjectCatalogChangeRef.current = onObjectCatalogChange
  useEffect(() => {
    onObjectCatalogChangeRef.current?.(objects, selectedUid)
  }, [objects, selectedUid])
  useEffect(() => {
    return () => {
      onObjectCatalogChangeRef.current?.([], null)
    }
  }, [])
  const [lightSelected, setLightSelected] = useState(false)
  const [gizmoMode, setGizmoMode] = useState<SceneGizmoMode>("translate")
  const [gizmoAnchor, setGizmoAnchor] = useState<{ x: number; y: number } | null>(null)
  const [lightAnchor, setLightAnchor] = useState<{ x: number; y: number } | null>(null)
  const [environment, setEnvironment] = useState(() => defaultSceneEnvironment())
  const [lightIntensity, setLightIntensity] = useState(1)
  const [lightIntensityRange, setLightIntensityRange] = useState({ min: 0.2, max: 5 })
  const [selectionExtrudeUi, setSelectionExtrudeUi] = useState<{
    visible: boolean
    kind: "sector" | "face"
    canExtrude: boolean
    canIntrude: boolean
  } | null>(null)
  const [selectionExtrudeAnchor, setSelectionExtrudeAnchor] = useState<PlatformSelectionToolbarAnchorScreen | null>(
    null,
  )
  const FLOOR_COLOR_DEFAULT = "#c7c7c7"
  const [floorColorOpen, setFloorColorOpen] = useState(false)
  const [floorColor, setFloorColor] = useState(FLOOR_COLOR_DEFAULT)
  const floorColorDirtyRef = useRef(false)
  const floorColorPendingHexRef = useRef<string | null>(null)
  const floorColorPopoverRef = useRef<HTMLDivElement>(null)
  const floorColorPanelRef = useRef<HTMLDivElement>(null)
  const [objectColorOpen, setObjectColorOpen] = useState(false)
  const [heroFigureOpen, setHeroFigureOpen] = useState(false)
  const heroFigurePopoverRef = useRef<HTMLDivElement>(null)
  const heroFigurePanelRef = useRef<HTMLDivElement>(null)
  const [objectColor, setObjectColor] = useState(PRIMITIVE_COLOR)
  const objectColorDirtyRef = useRef(false)
  const objectColorPendingHexRef = useRef<string | null>(null)
  const objectColorPopoverRef = useRef<HTMLDivElement>(null)
  const objectColorPanelRef = useRef<HTMLDivElement>(null)
  const objectColorUidRef = useRef<string | null>(null)
  const [flySpeed, setFlySpeed] = useState(4.5)
  const [fov, setFov] = useState(55)
  const [cameraPath, setCameraPath] = useState<CameraPath>(() => emptySceneLayout().camera_path)
  const [pathEdit, setPathEdit] = useState<PathEditUi>({
    active: false,
    draw: true,
    observeArmed: false,
    smooth: true,
    durationMs: DEFAULT_SPLINE_DURATION_MS,
    look: { mode: "tangent" },
    lookRecorded: false,
    lookRecording: false,
    lookRewriteArmed: false,
    lookThrough: false,
    objectPathUid: null,
    previewObjectPathUid: null,
  })
  const onObjectPathModeChangeRef = useRef(onObjectPathModeChange)
  onObjectPathModeChangeRef.current = onObjectPathModeChange
  useEffect(() => {
    onObjectPathModeChangeRef.current?.({
      editUid: pathEdit.objectPathUid,
      previewUid: pathEdit.previewObjectPathUid,
    })
  }, [pathEdit.objectPathUid, pathEdit.previewObjectPathUid])
  useEffect(() => {
    return () => {
      onObjectPathModeChangeRef.current?.({ editUid: null, previewUid: null })
    }
  }, [])
  const [shownSceneName, setShownSceneName] = useState(sceneName)
  const sceneNameSaveRef = useRef(0)
  useEffect(() => {
    if (sceneNameSaveRef.current > 0) return
    setShownSceneName(sceneName)
  }, [sceneName])
  const [recording, setRecording] = useState(false)
  const [recordArmed, setRecordArmed] = useState(false)
  const [overlayElapsedMs, setOverlayElapsedMs] = useState(0)
  const [flashKey, setFlashKey] = useState(0)
  const overlaySaveRef = useRef<(() => Promise<boolean>) | null>(null)
  const overlayRecordTickRef = useRef<(ms: number) => void>(() => {})
  const recordElapsedRef = useRef(0)
  const overlayPaintAtRef = useRef(0)
  overlayRecordTickRef.current = (ms: number) => {
    recordElapsedRef.current = ms
    const now = performance.now()
    if (now - overlayPaintAtRef.current < 250) return
    overlayPaintAtRef.current = now
    setOverlayElapsedMs(ms)
  }
  const [playing, setPlaying] = useState(false)
  const [previzExporting, setPrevizExporting] = useState(false)
  const [frameMask, setFrameMask] = useState<FrameViewfinderRect | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerFrom, setPickerFrom] = useState<"toolbar" | "assistant">("toolbar")
  const pickerFromRef = useRef<"toolbar" | "assistant">("toolbar")
  const openPickerFrom = useCallback((origin: "toolbar" | "assistant") => {
    const same = pickerFromRef.current === origin
    setPickerOpen((open) => (open && same ? false : true))
    pickerFromRef.current = origin
    setPickerFrom(origin)
  }, [])
  const [pickerPreviewRef, setPickerPreviewRef] = useState<CanvasReferenceItem | null>(null)
  const [resetOpen, setResetOpen] = useState(false)
  const [overwriteRecordOpen, setOverwriteRecordOpen] = useState(false)
  const [exists, setExists] = useState(false)
  const [autosaveEnabled, setAutosaveEnabled] = useState(() => sceneMode === "editPreviz" || scene3dId != null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [lightMenuOpen, setLightMenuOpen] = useState(false)
  const [lightPreset, setLightPreset] = useState<string>("south")
  const platformFormApiRef = useRef<PlatformFormApi | null>(null)
  const [platformFormChrome, setPlatformFormChrome] = useState<{
    draft: ScenePlatform
    busy: boolean
    canEdit: boolean
  } | null>(null)
  const [flyQrBusy, setFlyQrBusy] = useState(false)
  const [platformLayout, setPlatformLayout] = useState<SceneLayoutState | null>(null)
  const platformLayoutRef = useRef<SceneLayoutState | null>(null)
  platformLayoutRef.current = platformLayout
  const hasPendingFaces = useMemo(
    () => (platformLayout?.faces ?? []).some((face) => face.status === "pending"),
    [platformLayout?.faces],
  )
  const [phoneLayout, setPhoneLayout] = useState(readPhoneLayout)
  /** Engine created + layout fetch settled (QR auto-fly waits on this). */
  const [sceneBooted, setSceneBooted] = useState(false)
  const sceneBootedRef = useRef(false)
  sceneBootedRef.current = sceneBooted
  /** Drive tap-to-look hint; mirrors gyroReadyRef for render. */
  const [gyroReady, setGyroReady] = useState(false)

  const markGyroReady = useCallback(() => {
    gyroReadyRef.current = true
    setGyroReady(true)
  }, [])

  useEffect(() => {
    onExistsChange?.(exists)
  }, [exists, onExistsChange])
  const [deviceCameraActive, setDeviceCameraActive] = useState(false)
  const [cameraViewActive, setCameraViewActive] = useState(false)
  const cameraViewActiveRef = useRef(false)
  cameraViewActiveRef.current = cameraViewActive
  const [viewPlaybackEnded, setViewPlaybackEnded] = useState(false)
  const [goToStartBusy, setGoToStartBusy] = useState(false)
  const [photoBusy, setPhotoBusy] = useState(false)
  const onViewpointCaptureBusyChangeRef = useRef(onViewpointCaptureBusyChange)
  onViewpointCaptureBusyChangeRef.current = onViewpointCaptureBusyChange

  useEffect(() => {
    onViewpointCaptureBusyChangeRef.current?.(photoBusy || previzExporting)
  }, [photoBusy, previzExporting])

  useEffect(() => {
    return () => onViewpointCaptureBusyChangeRef.current?.(false)
  }, [])
  const [captureHintStep, setCaptureHintStep] = useState<CaptureHintStep | null>(null)
  const [hasStartCamera, setHasStartCamera] = useState(false)
  const [placePreview, setPlacePreview] = useState(false)
  const [cameraPlaceMode, setCameraPlaceMode] = useState(false)
  const modeSaveKey = cameraPlaceMode
    ? "place"
    : pathEdit.objectPathUid
      ? `object:${pathEdit.objectPathUid}`
      : pathEdit.active
        ? "camera"
        : ""
  const modeSaveBaselineRef = useRef<{
    key: string
    cameraPath: CameraPath
    start: SceneCameraState | null
    objectPath: string | null
  } | null>(null)
  useEffect(() => {
    if (!modeSaveKey) {
      modeSaveBaselineRef.current = null
      return
    }
    const uid = pathEdit.objectPathUid
    const objectPath = uid ? (objects.find((item) => item.uid === uid)?.path ?? null) : null
    modeSaveBaselineRef.current = {
      key: modeSaveKey,
      cameraPath: cloneCameraPath(cameraPath),
      start: cloneStartCamera(startCameraRef.current),
      objectPath: objectPathFingerprint(objectPath),
    }
    // Снимок только при входе в режим. Позже путь меняется, и кнопка должна это увидеть.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeSaveKey])
  const [cameraPlaceConfirmOpen, setCameraPlaceConfirmOpen] = useState(false)
  const [liveSyncBadge, setLiveSyncBadge] = useState<"idle" | "live" | "following">("idle")
  const [portalReady, setPortalReady] = useState(false)
  const [stageBox, setStageBox] = useState({ top: 0, left: 0, width: 0, height: 0 })

  useEffect(() => {
    existsRef.current = exists
  }, [exists])

  useEffect(() => {
    autosaveEnabledRef.current = autosaveEnabled
  }, [autosaveEnabled])

  const hasVideoRef = useRef(initialHasVideo)
  const initialPathExportDirty = Boolean(
    initialSceneState && hasCameraPath(initialSceneState.camera_path ?? null) && !initialHasVideo,
  )
  const pathExportDirtyRef = useRef(initialPathExportDirty)
  const syncPathExportDirty = useCallback((dirty: boolean) => {
    pathExportDirtyRef.current = dirty
  }, [])
  const syncPathExportDirtyRef = useRef(syncPathExportDirty)
  syncPathExportDirtyRef.current = syncPathExportDirty
  const savePathPrevizRef = useRef<() => Promise<boolean>>(async () => false)

  const {
    ensurePrevizDraft,
    ensurePrevizDraftRef,
    queuePersist,
    persistObjectsNow,
    uploadPosterIfNeeded,
    schedulePosterUpload,
    saveViewpointStill,
    syncPathAfterPhoneRecord,
    clearPersistTimer,
    getSceneStateForPreviz,
  } = useLocationScenePersist({
    projectId,
    locationId,
    locationName,
    sceneMode,
    scene3dId,
    canEdit,
    t: t as TranslateFn,
    onLayoutChanged,
    onPrevizSaved,
    onViewpointSaved,
    engineRef,
    platformVisualRef,
    startCameraRef,
    disposedRef,
    dirtyRef,
    existsRef,
    autosaveEnabledRef,
    boundScene3dIdRef,
    setBoundScene3dId,
    setExists,
    setAutosaveEnabled,
  })

  const handlePrevizExported = useCallback(
    (previz?: LibraryPrevizItem) => {
      syncPathExportDirty(false)
      hasVideoRef.current = true
      if (previz != null && !disposedRef.current) {
        setBoundPrevizId(previz.id)
        if (previz.scene3d_id != null) {
          boundScene3dIdRef.current = previz.scene3d_id
          setBoundScene3dId(previz.scene3d_id)
        }
      }
      onPrevizSaved?.(previz)
    },
    [onPrevizSaved, syncPathExportDirty],
  )

  const handleExportingChange = useCallback(
    (exporting: boolean) => {
      onExportingChange?.(exporting)
    },
    [onExportingChange],
  )

  const handleCapturingChange = useCallback((capturing: boolean) => {
    setPrevizExporting(capturing)
  }, [])

  const savePathPreviz = useCallback(async (): Promise<boolean> => {
    const engine = engineRef.current
    if (!engine || !canEdit || !hasCameraPath(engine.getCameraPath())) return false
    onPrevizCreateStart?.()
    handleExportingChange(true)
    handleCapturingChange(true)
    try {
      let layoutId = boundScene3dId ?? scene3dId
      if (layoutId == null) {
        layoutId = await ensurePrevizDraft({ force: true })
      }
      const result = await savePrevizFromEngine({
        engine,
        projectId,
        locationId,
        scene3dId: layoutId,
        previzName: sceneName,
        shotId,
        getSceneState: getSceneStateForPreviz,
        onCaptured: () => {
          if (!disposedRef.current) handleCapturingChange(false)
        },
      })
      if (!result.ok) {
        toast.error(t("locationsPage.scene3d.timeline.exportEmpty"))
        onPrevizSaveFailed?.()
        return false
      }
      setBoundPrevizId(result.previz.id)
      if (result.previz.scene3d_id != null) {
        boundScene3dIdRef.current = result.previz.scene3d_id
        setBoundScene3dId(result.previz.scene3d_id)
      }
      toast.success(t("locationsPage.scene3d.timeline.saved"))
      handlePrevizExported(result.previz)
      return true
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e))
      onPrevizSaveFailed?.()
      return false
    } finally {
      handleCapturingChange(false)
      handleExportingChange(false)
    }
  }, [
    boundScene3dId,
    canEdit,
    ensurePrevizDraft,
    getSceneStateForPreviz,
    handlePrevizExported,
    handleCapturingChange,
    handleExportingChange,
    onPrevizCreateStart,
    onPrevizSaveFailed,
    locationId,
    sceneName,
    projectId,
    scene3dId,
    shotId,
    t,
  ])
  savePathPrevizRef.current = savePathPreviz

  const agentFocus = useCallback((): Scene3dEditorFocus | null => {
    const engine = engineRef.current
    const id = boundScene3dIdRef.current ?? scene3dId
    if (!engine || id == null) return null
    const snapshot = engine.getSceneState()
    const visual = platformVisualRef.current
    const selectedGroupId =
      selectionPopoverAction(selectedUids, snapshot.objects) === "ungroup"
        ? snapshot.objects.find((obj) => obj.uid === selectedUids[0])?.group_id
        : undefined
    return {
      scene3d_id: id,
      dirty: dirtyRef.current,
      opened_from: shotId != null ? "shot" : "gallery",
      shot_id: shotId ?? null,
      selection: {
        object_uid: selectedUids[0] ?? null,
        ...(selectedGroupId ? { group_id: selectedGroupId } : {}),
        sectors: (visual?.selectedSectors ?? []).map((sector) => ({ x: sector.x, z: sector.z })),
        face_tiles: (visual?.selectedFaceTiles ?? []).map((tile) => ({
          face_key: tile.faceKey,
          u: tile.u,
          v: tile.v,
        })),
      },
      scene_state: sceneStateForSave({
        ...emptySceneLayout(),
        ...snapshot,
        start_camera: startCameraRef.current,
        platform: visual?.platform ?? null,
        cells: visual?.cells ?? [],
        openings: visual?.openings ?? [],
        light: visual?.light ?? null,
        ...(visual ? { faces: visual.faces ?? [] } : {}),
        exists: true,
      }),
    }
  }, [scene3dId, selectedUids, shotId])

  const focusObject = useCallback((uid: string) => {
    engineRef.current?.frameObject(uid)
  }, [])

  const editObjectPath = useCallback(
    (uid: string) => {
      const engine = engineRef.current
      if (!engine || !canEdit) return
      engine.selectObject(uid)
      engine.setObjectPathEdit(uid)
    },
    [canEdit],
  )

  const showObjectPath = useCallback((uid: string) => {
    engineRef.current?.showObjectPath(uid)
  }, [])

  const applyServerLayout = useCallback((raw: unknown) => {
    const engine = engineRef.current
    if (!engine || !raw || typeof raw !== "object") return
    const layout = normalizeSceneLayout(raw as Partial<SceneLayoutState>)
    const prev = platformVisualRef.current
    const platformVisual: PlatformLayerState = {
      platform: platformIsSet(layout.platform) ? layout.platform : null,
      cells: layout.cells ?? [],
      faces: layout.faces ?? [],
      openings: layout.openings ?? [],
      selectedSectors: prev?.selectedSectors ?? [],
      selectedFaceTiles: prev?.selectedFaceTiles ?? [],
      activeFaceKey: prev?.activeFaceKey ?? null,
      light: normalizeSceneLight(layout.light),
      surfaceGrid: prev?.surfaceGrid ?? false,
    }
    dirtyRef.current = false
    existsRef.current = layout.exists
    setExists(layout.exists)
    startCameraRef.current = layout.start_camera
    setHasStartCamera(layout.start_camera != null)
    skipHistoryRef.current = true
    try {
      platformVisualRef.current = platformVisual
      engine.applySceneState({ ...layout, camera_state: layout.camera_state })
      engine.setCameraPlaceAim(
        cameraPlaceAimFromStart(layout.start_camera, layout.camera_path.authoring?.points.length ?? 0),
      )
      engine.setPlatformState(platformVisual)
      setFov(Math.round(engine.getFov()))
      setEnvironment(normalizeSceneEnvironment(layout.environment))
      setPlatformLayout(layout)
      setObjects(layout.objects)
      setCameraPath(layout.camera_path)
      setSelectedUids((current) => current.filter((uid) => layout.objects.some((object) => object.uid === uid)))
    } finally {
      skipHistoryRef.current = false
    }
  }, [])

  useImperativeHandle(
    ref,
    () => ({
      requestReset: () => {
        if (!canEdit || !existsRef.current) return
        setResetOpen(true)
      },
      isPathExportDirty: () => pathExportDirtyRef.current,
      savePathPreviz: () => savePathPrevizRef.current(),
      uploadPosterIfNeeded: () => {
        if (engineRef.current?.isExporting()) return Promise.resolve(false)
        return uploadPosterIfNeeded()
      },
      isExporting: () => engineRef.current?.isExporting() ?? false,
      schedulePosterUpload: () => {
        if (!sceneBootedRef.current || engineRef.current?.isExporting()) return Promise.resolve(false)
        return schedulePosterUpload()
      },
      agentFocus,
      applyServerLayout,
      focusObject,
      editObjectPath,
      showObjectPath,
      captureViewpoint: () => captureViewpointActionRef.current(),
      toggleObjectPicker: () => toggleObjectPickerRef.current(),
    }),
    [
      applyServerLayout,
      agentFocus,
      canEdit,
      editObjectPath,
      focusObject,
      schedulePosterUpload,
      showObjectPath,
      uploadPosterIfNeeded,
    ],
  )

  const {
    historyEntries,
    redoStack,
    pushHistorySnapshot,
    pushHistorySnapshotRef,
    captureCurrentSnapshot,
    rewindHistoryTo,
    undoLastAction,
    redoLastAction,
    resetHistoryToBaseline,
    clearAndReloadHistory,
    clearHistoryRedo,
    loadOrSeedBaseline,
  } = useLocationSceneHistory({
    projectId,
    locationId,
    boundScene3dId,
    scene3dId,
    engineRef,
    platformVisualRef,
    startCameraRef,
    setHasStartCamera,
    skipHistoryRef,
    dirtyRef,
    existsRef,
    setExists,
    setObjects,
    setCameraPath,
    setPlatformLayout,
    setHistoryOpen,
    queuePersist,
  })
  clearHistoryRedoRef.current = clearHistoryRedo

  useEffect(() => {
    if (typeof window === "undefined") return
    const mq = window.matchMedia(DEVICE_CAMERA_MQ)
    const sync = () => {
      setPhoneLayout(mq.matches)
    }
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  useEffect(() => {
    engineRef.current?.setPathEditAllowed(!phoneLayout)
  }, [phoneLayout, sceneBooted])

  useEffect(() => {
    return () => {
      deviceLookRef.current?.stop()
      deviceLookRef.current = null
    }
  }, [])

  const disableDeviceOrientationLook = useCallback(() => {
    deviceLookRef.current?.stop()
    deviceLookRef.current = null
  }, [])

  /** Subscribe gyro after permission already granted (no requestPermission). */
  const attachDeviceOrientationLook = useCallback(
    (session: number, onFirstSample?: () => void) => {
      const engine = engineRef.current
      if (!engine || session !== deviceCamSessionRef.current) return false

      disableDeviceOrientationLook()
      const liveEngine = engine
      deviceLookRef.current = startDeviceOrientationLook({
        getCameraLook: () => liveEngine.getFlyLook(),
        getMode: () => deviceLookModeRef.current,
        onFirstSample,
        onLook: (look) => {
          if (deviceLookModeRef.current === "full") {
            liveEngine.setFlyLook(look.yaw, look.pitch)
            return
          }
          // pitchOnly: stick owns yaw; phone tilt only updates pitch.
          const current = liveEngine.getFlyLook()
          liveEngine.setFlyLook(current.yaw, look.pitch)
        },
      })
      deviceLookRef.current.calibrate(liveEngine.getFlyLook())
      return true
    },
    [disableDeviceOrientationLook],
  )

  const applyFlyInputMerge = useCallback(() => {
    engineRef.current?.setFlyInput({ ...flyInputMergeRef.current })
  }, [])

  const restoreStartCameraLocal = useCallback(() => {
    const engine = engineRef.current
    const cam = startCameraRef.current
    if (!engine || !cam) return
    livePoseSmootherRef.current?.reset()
    engine.applyCameraState(cam)
  }, [])

  /** After phone-fly: start_camera if set, else pose from enter. Keep current nav mode. */
  const restoreAfterDeviceCamera = useCallback(() => {
    const engine = engineRef.current
    if (!engine) return
    const cam = startCameraRef.current ?? preDeviceCamRef.current
    if (!cam) return
    livePoseSmootherRef.current?.reset()
    const keepMode = engine.getNavMode()
    engine.applyCameraState({ ...cam, mode: keepMode })
    preDeviceCamRef.current = null
  }, [])

  const endFollowingSession = useCallback(() => {
    if (followingBadgeTimerRef.current != null) {
      window.clearTimeout(followingBadgeTimerRef.current)
      followingBadgeTimerRef.current = null
    }
    const wasFollowing = followingActiveRef.current
    followingActiveRef.current = false
    setLiveSyncBadge((prev) => (prev === "following" ? "idle" : prev))
    if (wasFollowing && !deviceCameraActiveRef.current) {
      restoreStartCameraLocal()
    }
  }, [restoreStartCameraLocal])

  const stopDeviceCamera = useCallback(() => {
    void (async () => {
      // Invalidate in-flight start/permission before cleanup.
      deviceCamSessionRef.current += 1
      const wasActive = deviceCameraActiveRef.current
      // Sync ref before any await so concurrent enter/stop sees truth.
      deviceCameraActiveRef.current = false
      const engine = engineRef.current
      let notifyPath = false

      const viewModal = deviceLookModeRef.current === "full"
      if (wasActive && engine && viewModal && engine.isRecording()) phoneLookRewrittenRef.current = true
      const keepRecordedLook = phoneLookRewrittenRef.current
      if (wasActive && engine) {
        const wasRecording = engine.isRecording()
        if (wasRecording || engine.isRecordingArmed()) {
          engine.stopRecording()
        }
        // Проход мог уже кончиться: отложенный queuePersist (1.5 с) не должен потеряться при закрытии.
        if (canEdit && (wasRecording || viewModal)) {
          notifyPath = await syncPathAfterPhoneRecord()
        }
      }

      disableDeviceOrientationLook()
      gyroReadyRef.current = false
      gyroAttachInFlightRef.current = false
      setGyroReady(false)
      flyInputMergeRef.current = { ...EMPTY_FLY_MERGE }
      lastStageTapRef.current = 0
      engineRef.current?.clearFlyInput()
      engineRef.current?.setLookYawRate(0)
      if (viewModal) engineRef.current?.stopPlayback()
      if (keepRecordedLook) {
        viewModalLookThroughRef.current = false
        engineRef.current?.discardLookPreview()
      } else if (viewModalLookThroughRef.current) {
        engineRef.current?.setPathLookThrough(false)
        viewModalLookThroughRef.current = false
      }
      engineRef.current?.setDeviceLookFollowsPath(false)
      engineRef.current?.setDeviceLookActive(false)
      deviceLookModeRef.current = "pitchOnly"
      setCameraViewActive(false)
      setViewPlaybackEnded(false)

      if (notifyPath) {
        liveSyncRef.current?.notifyPathReady()
      }

      setDeviceCameraActive(false)
      if (wasActive) {
        if (!keepRecordedLook) restoreAfterDeviceCamera()
        phoneLookRewrittenRef.current = false
        onDeviceCameraExitRef.current?.()
      }
      // Placeholder may still be 0×0 until flex settles — force remeasure after paint.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          rememeasureStageBoxRef.current()
        })
      })
    })()
  }, [canEdit, disableDeviceOrientationLook, restoreAfterDeviceCamera, syncPathAfterPhoneRecord])

  useEffect(() => {
    // Phone autostart keeps the shell in the viewport while the scene is still loading.
    // Reporting inactive here pulls the panel back into the slot and starts the scene again.
    if (!deviceCameraActive && canEdit && (autoStartDeviceCamera || autoStartCameraView)) {
      onImmersiveChange?.(true)
      return
    }
    onImmersiveChange?.(deviceCameraActive)
  }, [autoStartCameraView, autoStartDeviceCamera, canEdit, deviceCameraActive, onImmersiveChange])

  useEffect(() => {
    if (!deviceCameraActive) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      event.preventDefault()
      event.stopPropagation()
      stopDeviceCamera()
    }
    window.addEventListener("keydown", onKeyDown, true)
    return () => window.removeEventListener("keydown", onKeyDown, true)
  }, [deviceCameraActive, stopDeviceCamera])

  const showFrameMask =
    !previzExporting &&
    ((!phoneLayout && (recordArmed || recording || pathEdit.lookThrough)) || (phoneLayout && deviceCameraActive))

  useLayoutEffect(() => {
    if (!showFrameMask) {
      setFrameMask(null)
      return
    }
    const el = stageRef.current
    if (!el) return
    const aspect = frameFormatRatio(frame)
    const read = () => {
      const w = el.clientWidth
      const h = el.clientHeight
      if (w < 32 || h < 32) {
        setFrameMask(null)
        return
      }
      // Phone modal: the stage is still the editor slot until stageBox catches the visual viewport.
      // Painting that short rect leaves dark bands in Safari after the modal closes.
      if (deviceCameraActiveRef.current) {
        const box = readDeviceFlyViewportBox()
        if (Math.abs(w - box.width) > 4 || Math.abs(h - box.height) > 4) {
          setFrameMask(null)
          return
        }
      }
      const next = frameViewfinderRect(w, h, aspect)
      setFrameMask((prev) =>
        prev && prev.x === next.x && prev.y === next.y && prev.width === next.width && prev.height === next.height
          ? prev
          : next,
      )
    }
    read()
    const ro = new ResizeObserver(read)
    ro.observe(el)
    return () => ro.disconnect()
  }, [showFrameMask, frame, stageBox.width, stageBox.height])

  useLayoutEffect(() => {
    setPortalReady(true)
  }, [])

  useLayoutEffect(() => {
    const read = () => {
      // Phone-fly: cover the *visual* viewport in CSS pixels (not svh/dvh — flaky on iOS first paint).
      if (deviceCameraActiveRef.current) {
        setStageBox(readDeviceFlyViewportBox())
        return
      }
      const el = placeholderRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      // Scene zone is the flex slot under the toolbar. Only flip the visibility threshold, not every size tick.
      // Engine ResizeObserver owns canvas setSize; React stageBox is for stageVisible / portal layout.
      setStageBox((prev) => {
        const nextBig = r.width >= 32 && r.height >= 32
        const prevBig = prev.width >= 32 && prev.height >= 32
        if (nextBig === prevBig) return prev
        if (!nextBig) return prev
        return { top: 0, left: 0, width: r.width, height: r.height }
      })
    }
    read()
    rememeasureStageBoxRef.current = read
    const el = placeholderRef.current
    const parent = el?.parentElement ?? null
    const ro = el || parent ? new ResizeObserver(read) : null
    if (el && ro) ro.observe(el)
    // cqw/cqh size the placeholder from the container — observe parent too.
    if (parent && ro) ro.observe(parent)
    window.addEventListener("resize", read)
    const vv = window.visualViewport
    const immersive = deviceCameraActive
    if (immersive) {
      window.addEventListener("scroll", read, true)
      vv?.addEventListener("resize", read)
      vv?.addEventListener("scroll", read)
    }
    return () => {
      ro?.disconnect()
      window.removeEventListener("resize", read)
      if (immersive) {
        window.removeEventListener("scroll", read, true)
        vv?.removeEventListener("resize", read)
        vv?.removeEventListener("scroll", read)
      }
    }
  }, [deviceCameraActive, frame, objects.length])

  // iOS: visualViewport settles late after entering fly (rotate/refresh "fixes" it).
  useLayoutEffect(() => {
    if (!deviceCameraActive) return

    const bump = () => {
      const next = readDeviceFlyViewportBox()
      setStageBox((prev) =>
        prev.top === next.top && prev.left === next.left && prev.width === next.width && prev.height === next.height
          ? prev
          : next,
      )
      rememeasureStageBoxRef.current()
      requestAnimationFrame(() => {
        const stage = stageRef.current
        if (stage && stage.clientWidth >= 32 && stage.clientHeight >= 32) {
          engineRef.current?.resize()
        }
      })
    }

    bump()
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      bump()
      raf2 = requestAnimationFrame(bump)
    })
    const t1 = window.setTimeout(bump, 50)
    const t2 = window.setTimeout(bump, 200)
    const t3 = window.setTimeout(bump, 500)
    const t4 = window.setTimeout(bump, 1000)

    const stage = stageRef.current
    const ro = stage ? new ResizeObserver(() => bump()) : null
    if (stage && ro) ro.observe(stage)

    window.addEventListener("orientationchange", bump)
    document.addEventListener("visibilitychange", bump)
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
      window.clearTimeout(t1)
      window.clearTimeout(t2)
      window.clearTimeout(t3)
      window.clearTimeout(t4)
      ro?.disconnect()
      window.removeEventListener("orientationchange", bump)
      document.removeEventListener("visibilitychange", bump)
    }
  }, [deviceCameraActive])

  // QR / cold open: panel + placeholder settle late; ResizeObserver alone misses position-only shifts.
  useLayoutEffect(() => {
    if (deviceCameraActive) return

    const bump = () => {
      rememeasureStageBoxRef.current()
      requestAnimationFrame(() => {
        const stage = stageRef.current
        if (stage && stage.clientWidth >= 32 && stage.clientHeight >= 32) {
          engineRef.current?.resize()
        }
      })
    }

    bump()
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      bump()
      raf2 = requestAnimationFrame(bump)
    })
    const t1 = window.setTimeout(bump, 50)
    const t2 = window.setTimeout(bump, 200)
    const t3 = window.setTimeout(bump, 500)
    const t4 = window.setTimeout(bump, 1000)

    const el = placeholderRef.current
    const parent = el?.parentElement ?? null
    const ro = el || parent ? new ResizeObserver(bump) : null
    if (el && ro) ro.observe(el)
    if (parent && ro) ro.observe(parent)

    window.addEventListener("orientationchange", bump)
    document.addEventListener("visibilitychange", bump)
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
      window.clearTimeout(t1)
      window.clearTimeout(t2)
      window.clearTimeout(t3)
      window.clearTimeout(t4)
      ro?.disconnect()
      window.removeEventListener("orientationchange", bump)
      document.removeEventListener("visibilitychange", bump)
    }
  }, [deviceCameraActive, frame, objects.length])

  useEffect(() => {
    liveSyncRef.current?.setPublisher(deviceCameraActive)
    if (deviceCameraActive) {
      followingActiveRef.current = false
      livePoseSmootherRef.current?.reset()
      setLiveSyncBadge("live")
      if (followingBadgeTimerRef.current != null) {
        window.clearTimeout(followingBadgeTimerRef.current)
        followingBadgeTimerRef.current = null
      }
    } else {
      setLiveSyncBadge((prev) => (prev === "live" ? "idle" : prev))
    }
  }, [deviceCameraActive])

  useEffect(() => {
    const syncScene3dId = boundScene3dId ?? scene3dId ?? null
    if (syncScene3dId == null && (locationId == null || locationId <= 0)) return

    const clientId = crypto.randomUUID()
    followingActiveRef.current = false
    const smoother = createCameraLivePoseSmoother({
      onApply: (pose) => {
        if (deviceCameraActiveRef.current) return
        const engine = engineRef.current
        if (!engine || engine.isPlaying()) return
        engine.applyCameraState({ ...pose, mode: "fly" })
      },
    })
    livePoseSmootherRef.current = smoother

    const pullRemotePath = async () => {
      const applyPath = (path: ReturnType<typeof normalizeSceneLayout>["camera_path"]) => {
        if (!hasCameraPath(path)) return
        const engine = engineRef.current
        if (!engine || deviceCameraActiveRef.current) return
        engine.replacePath(path)
        setCameraPath(path)
      }
      const loadOnce = async () => {
        const id = boundScene3dIdRef.current
        if (id == null) return
        const detail = await fetchScene3d(projectId, id)
        applyPath(normalizeSceneLayout(detail.scene_state).camera_path)
      }
      try {
        await loadOnce()
      } catch {
        try {
          await loadOnce()
        } catch (e) {
          toast.error(e instanceof Error ? e.message : String(e))
        }
      }
    }

    const sync = createCameraLiveSync({
      projectId,
      scene3dId: syncScene3dId,
      locationId: syncScene3dId == null ? locationId : null,
      clientId,
      isPublisher: deviceCameraActiveRef.current,
      getPose: () => {
        const engine = engineRef.current
        if (!engine) return null
        const cam = engine.getSceneState().camera_state
        return { pos: cam.pos, yaw: cam.yaw, pitch: cam.pitch, fov: cam.fov }
      },
      onRemotePose: (pose) => {
        if (deviceCameraActiveRef.current) return
        const engine = engineRef.current
        if (!engine || engine.isPlaying()) return
        const justConnected = !followingActiveRef.current
        if (justConnected && !engine.isPathLookThrough()) {
          engine.setPathLookThrough(true)
        }
        followingActiveRef.current = true
        smoother.setTarget(pose)
        engineRef.current?.setNavMode("fly")
        setFov(Math.round(pose.fov))
        setLiveSyncBadge("following")
        if (followingBadgeTimerRef.current != null) window.clearTimeout(followingBadgeTimerRef.current)
        followingBadgeTimerRef.current = window.setTimeout(() => {
          followingBadgeTimerRef.current = null
          endFollowingSession()
        }, 1600)
      },
      onRemoteSessionEnd: () => {
        if (deviceCameraActiveRef.current) return
        endFollowingSession()
      },
      onRemotePathReady: () => {
        if (deviceCameraActiveRef.current) return
        void pullRemotePath()
      },
    })
    liveSyncRef.current = sync
    return () => {
      sync.dispose()
      liveSyncRef.current = null
      smoother.dispose()
      livePoseSmootherRef.current = null
      followingActiveRef.current = false
      if (followingBadgeTimerRef.current != null) {
        window.clearTimeout(followingBadgeTimerRef.current)
        followingBadgeTimerRef.current = null
      }
    }
  }, [boundScene3dId, endFollowingSession, locationId, projectId, scene3dId])

  /** Sync phone chrome + engine prep (no orientation permission). */
  const enterDeviceCameraUi = useCallback(
    (launch: "fly" | "view" = "fly") => {
      if (!canEdit) return false
      const engine = engineRef.current
      if (!engine) {
        toast.error(t("locationsPage.scene3d.deviceCamera.notReady"))
        return false
      }

      lastStageTapRef.current = 0
      preDeviceCamRef.current = { ...engine.getSceneState().camera_state }
      flyInputMergeRef.current = { ...EMPTY_FLY_MERGE }
      engine.clearFlyInput()
      engine.setFlyAnalogAxes({ strafe: 0, vertical: 0 })
      engine.setLookYawRate(0)
      deviceLookModeRef.current = launch === "view" ? "full" : "pitchOnly"
      engine.setDeviceLookFollowsPath(launch === "view")
      if (launch === "fly") {
        engine.setFlySpeed(DEVICE_FLY_DEFAULT_SPEED)
        setFlySpeed(DEVICE_FLY_DEFAULT_SPEED)
        rightStickBaseSpeedRef.current = DEVICE_FLY_DEFAULT_SPEED
      }
      engine.setDeviceLookActive(true)
      if (launch === "view") {
        if (!engine.isPathLookThrough()) {
          viewModalLookThroughRef.current = true
          engine.setPathLookThrough(true)
        } else {
          viewModalLookThroughRef.current = false
        }
      } else {
        viewModalLookThroughRef.current = false
      }
      setPickerOpen(false)
      // Sync ref before paint so stop/guards see active immediately.
      deviceCameraActiveRef.current = true
      flyEnterAtRef.current = performance.now()
      gyroReadyRef.current = false
      setGyroReady(false)
      setCameraViewActive(launch === "view")
      setDeviceCameraActive(true)
      onPhoneFlyEnterRef.current?.()
      queuePersist()
      return true
    },
    [canEdit, queuePersist, t],
  )

  /**
   * Lazy gyro for QR auto-fly (permission needs a user gesture on iOS).
   * Deny/unsupported → toast once; sticks keep working.
   * Must run in the same sync turn as a click / pointer gesture.
   */
  const ensureDeviceOrientationLook = useCallback(() => {
    if (!deviceCameraActiveRef.current) return
    if (gyroReadyRef.current || deviceLookRef.current) {
      markGyroReady()
      return
    }
    if (gyroAttachInFlightRef.current) return

    gyroAttachInFlightRef.current = true
    // Start permission in this sync turn (iOS / WebKit user activation).
    const permissionPromise = requestDeviceOrientationPermission()
    const session = ++deviceCamSessionRef.current

    void (async () => {
      try {
        const permission = await permissionPromise
        if (session !== deviceCamSessionRef.current) return
        if (permission === "unsupported") {
          toast.error(t("locationsPage.scene3d.deviceCamera.unsupported"))
          markGyroReady()
          return
        }
        if (permission === "denied") {
          toast.error(t("locationsPage.scene3d.deviceCamera.permissionDenied"))
          markGyroReady()
          return
        }
        let gotSample = false
        if (
          !attachDeviceOrientationLook(session, () => {
            gotSample = true
          })
        ) {
          return
        }
        markGyroReady()
        window.setTimeout(() => {
          if (session !== deviceCamSessionRef.current) return
          if (gotSample) return
          toast.error(t("locationsPage.scene3d.deviceCamera.unsupported"))
        }, 800)
      } finally {
        gyroAttachInFlightRef.current = false
      }
    })()
  }, [attachDeviceOrientationLook, markGyroReady, t])

  useEffect(() => {
    if (!autoStartDeviceCamera) flyAutoStartedRef.current = false
  }, [autoStartDeviceCamera])

  useEffect(() => {
    flyAutoStartedRef.current = false
  }, [locationId])

  // QR: after modal + stage settle, enter phone-fly once (gyro waits for first gesture).
  useEffect(() => {
    if (!autoStartDeviceCamera || !canEdit || deviceCameraActive) return
    if (flyAutoStartedRef.current) return
    if (!sceneBooted || stageBox.width < 32 || stageBox.height < 32) return
    if (!engineRef.current) return

    flyAutoStartedRef.current = true
    if (!enterDeviceCameraUi()) {
      flyAutoStartedRef.current = false
    }
  }, [
    autoStartDeviceCamera,
    canEdit,
    deviceCameraActive,
    sceneBooted,
    stageBox.width,
    stageBox.height,
    enterDeviceCameraUi,
  ])

  const viewAutoStartedRef = useRef(false)

  useEffect(() => {
    if (sceneBooted) return
    flyAutoStartedRef.current = false
    viewAutoStartedRef.current = false
  }, [sceneBooted])

  useEffect(() => {
    if (!autoStartCameraView) viewAutoStartedRef.current = false
  }, [autoStartCameraView])

  useEffect(() => {
    if (!autoStartCameraView || autoStartDeviceCamera || !canEdit || deviceCameraActive) return
    if (viewAutoStartedRef.current) return
    if (!sceneBooted || stageBox.width < 32 || stageBox.height < 32) return
    if (!engineRef.current) return

    viewAutoStartedRef.current = true
    if (!enterDeviceCameraUi("view")) {
      viewAutoStartedRef.current = false
    }
  }, [
    autoStartCameraView,
    autoStartDeviceCamera,
    canEdit,
    deviceCameraActive,
    sceneBooted,
    stageBox.width,
    stageBox.height,
    enterDeviceCameraUi,
  ])

  const togglePhoneFlyRecord = useCallback(() => {
    const engine = engineRef.current
    if (!engine || !canEdit) return
    if (recording || recordArmed) {
      const wasRecording = engine.isRecording()
      engine.stopRecording()
      if (wasRecording) {
        void (async () => {
          const ok = await syncPathAfterPhoneRecord()
          if (ok) liveSyncRef.current?.notifyPathReady()
        })()
      }
      return
    }
    engine.startRecording()
  }, [canEdit, recording, recordArmed, syncPathAfterPhoneRecord])

  const togglePhoneLookRewrite = useCallback(() => {
    const engine = engineRef.current
    if (!engine || !canEdit) return
    if (engine.isRecording()) {
      engine.stopRecording()
      return
    }
    engine.startLookRewrite()
  }, [canEdit])

  const lookRewriteWasRecordingRef = useRef(false)
  useEffect(() => {
    if (recording && cameraViewActive) {
      lookRewriteWasRecordingRef.current = true
      phoneLookRewrittenRef.current = true
      return
    }
    if (!lookRewriteWasRecordingRef.current) return
    lookRewriteWasRecordingRef.current = false
    void (async () => {
      const ok = await syncPathAfterPhoneRecord()
      if (ok) liveSyncRef.current?.notifyPathReady()
    })()
  }, [cameraViewActive, recording, syncPathAfterPhoneRecord])

  const onLeftStickPress = useCallback(() => {
    ensureDeviceOrientationLook()
    engineRef.current?.setFlyAnalogAxes({ strafe: 0, vertical: 0 })
  }, [ensureDeviceOrientationLook])

  const onLeftStickMove = useCallback(({ dx, dy }: { dx: number; dy: number; radius: number }) => {
    const engine = engineRef.current
    if (!engine) return
    const dead = DEVICE_FLY_STICK_DEADZONE_PX
    const speedFromMag = (mag: number) =>
      Math.min(4.5, Math.max(0.5, DEVICE_FLY_DEFAULT_SPEED + mag * DEVICE_FLY_SPEED_PER_PX))

    const absX = Math.abs(dx)
    const absY = Math.abs(dy)
    const strafe = absX > dead ? Math.sign(dx) * speedFromMag(absX) : 0
    // Screen up (dy < 0) → fly up (+)
    const vertical = absY > dead ? -Math.sign(dy) * speedFromMag(absY) : 0
    engine.setFlyAnalogAxes({ strafe, vertical })
  }, [])

  const onLeftStickRelease = useCallback(() => {
    engineRef.current?.setFlyAnalogAxes({ strafe: 0, vertical: 0 })
  }, [])

  const onRightStickPress = useCallback(() => {
    ensureDeviceOrientationLook()
    const engine = engineRef.current
    const base = engine?.getFlySpeed() ?? flySpeed
    rightStickBaseSpeedRef.current = base
  }, [ensureDeviceOrientationLook, flySpeed])

  const onRightStickMove = useCallback(
    ({ dx, dy }: { dx: number; dy: number; radius: number }) => {
      const engine = engineRef.current
      if (!engine) return
      const dead = DEVICE_FLY_STICK_DEADZONE_PX
      const thrust = Math.abs(dy) > dead
      const forward = thrust && dy < 0
      const backward = thrust && dy > 0
      const mag = thrust ? Math.abs(dy) : 0
      const nextSpeed = Math.min(4.5, Math.max(0.5, rightStickBaseSpeedRef.current + mag * DEVICE_FLY_SPEED_PER_PX))
      engine.setFlySpeed(nextSpeed)

      flyInputMergeRef.current = {
        ...flyInputMergeRef.current,
        forward,
        backward,
      }
      applyFlyInputMerge()

      const absX = Math.abs(dx)
      if (absX <= dead) {
        engine.setLookYawRate(0)
      } else {
        const rate = Math.min(DEVICE_FLY_MAX_YAW_RATE, absX * DEVICE_FLY_YAW_PER_PX)
        engine.setLookYawRate(-Math.sign(dx) * rate)
      }
    },
    [applyFlyInputMerge],
  )

  const onRightStickRelease = useCallback(() => {
    flyInputMergeRef.current = {
      ...flyInputMergeRef.current,
      forward: false,
      backward: false,
    }
    applyFlyInputMerge()
    const engine = engineRef.current
    engine?.setLookYawRate(0)
    const resetSpeed = rightStickBaseSpeedRef.current
    engine?.setFlySpeed(resetSpeed)
    setFlySpeed(resetSpeed)
  }, [applyFlyInputMerge])

  const onImmersiveStagePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (e.button !== 0) return
      e.preventDefault()
      ensureDeviceOrientationLook()
      const now = performance.now()
      if (now - flyEnterAtRef.current < DEVICE_FLY_ENTER_GRACE_MS) return
      if (now - lastStageTapRef.current < DEVICE_FLY_DOUBLE_TAP_MS) {
        lastStageTapRef.current = 0
        stopDeviceCamera()
        return
      }
      lastStageTapRef.current = now
    },
    [ensureDeviceOrientationLook, stopDeviceCamera],
  )

  const setSceneGizmoMode = useCallback((mode: SceneGizmoMode) => {
    setGizmoMode(mode)
    engineRef.current?.setGizmoMode(mode)
  }, [])

  const groupedSelectionKey = groupAction === "ungroup" ? [...selectedUids].sort().join("\0") : ""
  // A newly selected group starts in translate; the key stays put while rotate is on.
  useEffect(() => {
    if (!groupedSelectionKey) return
    setGizmoMode("translate")
    engineRef.current?.setGizmoMode("translate")
  }, [groupedSelectionKey])

  // Drop rotate/scale when selection clears so the next pick starts in translate.
  useEffect(() => {
    if (selectedUid != null) return
    setGizmoMode("translate")
    engineRef.current?.setGizmoMode("translate")
  }, [selectedUid])

  const lightIntensityDraggingRef = useRef(false)

  const handlePlatformVisual = useCallback((state: PlatformLayerState) => {
    const light = platformVisualRef.current?.light ?? state.light
    const next = { ...state, light }
    platformVisualRef.current = next
    engineRef.current?.setPlatformState(next)
  }, [])

  const handleBindSelectionExtrude = useCallback((api: SelectionExtrudeApi | null) => {
    selectionExtrudeRef.current = api
    setSelectionExtrudeUi((prev) => {
      if (!api) return prev == null ? prev : null
      if (
        prev &&
        prev.visible === api.visible &&
        prev.kind === api.kind &&
        prev.canExtrude === api.canExtrude &&
        prev.canIntrude === api.canIntrude
      ) {
        return prev
      }
      return { visible: api.visible, kind: api.kind, canExtrude: api.canExtrude, canIntrude: api.canIntrude }
    })
  }, [])

  const closeFloorColorPopover = useCallback(() => {
    if (!floorColorOpen) return
    const hex = floorColorPendingHexRef.current ?? floorColor
    setFloorColorOpen(false)
    if (floorColorDirtyRef.current) {
      floorColorDirtyRef.current = false
      void selectionExtrudeRef.current?.commitSolidColor?.(hex)
    }
  }, [floorColor, floorColorOpen])

  const previewFloorColor = useCallback((hex: string) => {
    floorColorPendingHexRef.current = hex
    setFloorColor(hex)
    floorColorDirtyRef.current = true
    selectionExtrudeRef.current?.previewSolidColor?.(hex)
  }, [])

  useEffect(() => {
    if (selectionExtrudeUi?.kind === "sector" && selectionExtrudeUi.visible) return
    if (floorColorOpen && floorColorDirtyRef.current) {
      void selectionExtrudeRef.current?.commitSolidColor?.(floorColorPendingHexRef.current ?? floorColor)
      floorColorDirtyRef.current = false
    }
    setFloorColorOpen(false)
  }, [selectionExtrudeUi, floorColor, floorColorOpen])

  useEffect(() => {
    if (!floorColorOpen) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      const inButton = floorColorPopoverRef.current?.contains(target) === true
      const inPanel = floorColorPanelRef.current?.contains(target) === true
      if (!inButton && !inPanel) closeFloorColorPopover()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeFloorColorPopover()
    }
    document.addEventListener("pointerdown", onPointerDown, true)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [floorColorOpen, closeFloorColorPopover])

  const previewObjectColor = useCallback(
    (hex: string) => {
      objectColorPendingHexRef.current = hex
      setObjectColor(hex)
      objectColorDirtyRef.current = true
      if (selectedUid == null) return
      engineRef.current?.setObjectColor(selectedUid, hex, { emit: false })
    },
    [selectedUid],
  )

  const closeObjectColorPopover = useCallback(() => {
    if (!objectColorOpen) return
    const hex = objectColorPendingHexRef.current ?? objectColor
    setObjectColorOpen(false)
    if (objectColorDirtyRef.current && selectedUid != null) {
      objectColorDirtyRef.current = false
      engineRef.current?.setObjectColor(selectedUid, hex, { emit: true })
    }
  }, [objectColor, objectColorOpen, selectedUid])

  useEffect(() => {
    const prevUid = objectColorUidRef.current
    if (prevUid && prevUid !== selectedUid && objectColorDirtyRef.current) {
      const hex = objectColorPendingHexRef.current ?? objectColor
      objectColorDirtyRef.current = false
      engineRef.current?.setObjectColor(prevUid, hex, { emit: true })
    }
    objectColorUidRef.current = selectedUid
    setObjectColorOpen(false)
    setHeroFigureOpen(false)
    const selected = objects.find((item) => item.uid === selectedUid)
    setObjectColor(selected?.color ?? PRIMITIVE_COLOR)
    objectColorPendingHexRef.current = null
    // Swatch follows the selection only. A color commit refreshes `objects` and must not reopen or reset the drag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUid])

  useEffect(() => {
    if (gizmoAnchor) return
    closeObjectColorPopover()
    setHeroFigureOpen(false)
  }, [closeObjectColorPopover, gizmoAnchor])

  useEffect(() => {
    if (!objectColorOpen) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      const inButton = objectColorPopoverRef.current?.contains(target) === true
      const inPanel = objectColorPanelRef.current?.contains(target) === true
      if (!inButton && !inPanel) closeObjectColorPopover()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeObjectColorPopover()
    }
    document.addEventListener("pointerdown", onPointerDown, true)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [closeObjectColorPopover, objectColorOpen])

  useEffect(() => {
    if (!heroFigureOpen) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      const inButton = heroFigurePopoverRef.current?.contains(target) === true
      const inPanel = heroFigurePanelRef.current?.contains(target) === true
      if (!inButton && !inPanel) setHeroFigureOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setHeroFigureOpen(false)
    }
    document.addEventListener("pointerdown", onPointerDown, true)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [heroFigureOpen])

  const heroFigureLabel = useCallback(
    (figure: SceneCharacterFigure) => {
      if (figure === "humanoid" || figure === "quadruped" || figure === "biped") {
        return t(`locationsPage.scene3d.figures.${figure}`)
      }
      return t(`locationsPage.scene3d.primitive.${figure}`)
    },
    [t],
  )

  const objectColorDefault = useMemo(() => {
    const selected = objects.find((item) => item.uid === selectedUid)
    if (selected?.kind === "character" && selected.entity_id != null) {
      return paletteColorFor(
        "character",
        selected.entity_id,
        cast,
        props,
        occupiedColorsForEntity(objects, "character", selected.entity_id),
      )
    }
    if (selected?.kind === "prop" && selected.entity_id != null) {
      return paletteColorFor(
        "prop",
        selected.entity_id,
        cast,
        props,
        occupiedColorsForEntity(objects, "prop", selected.entity_id),
      )
    }
    return PRIMITIVE_COLOR
  }, [cast, objects, props, selectedUid])

  const handleBindLightPresets = useCallback((api: LightPresetsApi | null) => {
    lightPresetsRef.current = api
    setLightPreset(api?.preset ?? "south")
  }, [])

  const handleBindPlatformForm = useCallback((api: PlatformFormApi | null) => {
    platformFormApiRef.current = api
    // Avoid setState on every bind: apply/setDraft identity churned via onLayoutApplied
    // and would infinite-loop if we stored the whole api object in React state.
    setPlatformFormChrome((prev) => {
      if (!api) return prev == null ? prev : null
      const next = { draft: api.draft, busy: api.busy, canEdit: api.canEdit }
      if (
        prev &&
        prev.busy === next.busy &&
        prev.canEdit === next.canEdit &&
        prev.draft.sectors_x === next.draft.sectors_x &&
        prev.draft.sectors_z === next.draft.sectors_z &&
        prev.draft.unit === next.draft.unit &&
        prev.draft.sector_size === next.draft.sector_size
      ) {
        return prev
      }
      return next
    })
  }, [])

  const handleLightChange = useCallback(
    (light: SceneLightState) => {
      const prev = platformVisualRef.current
      if (prev) platformVisualRef.current = { ...prev, light }
      setPlatformLayout((layout) => (layout ? { ...layout, light } : layout))
      dirtyRef.current = true
      existsRef.current = true
      setExists(true)
      pushHistorySnapshot("locationsPage.scene3d.historyActions.light", {
        ...captureCurrentSnapshot(),
        light,
      })
      if (!autosaveEnabledRef.current) return
      if (persistLightTimerRef.current != null) window.clearTimeout(persistLightTimerRef.current)
      persistLightTimerRef.current = window.setTimeout(() => {
        void (async () => {
          try {
            const id = await ensurePrevizDraftRef.current({ force: true })
            if (id == null || disposedRef.current) return
            const visual = platformVisualRef.current
            const payload = sceneStateForSave({
              ...emptySceneLayout(),
              ...engineRef.current?.getSceneState(),
              start_camera: startCameraRef.current,
              platform: visual?.platform ?? null,
              cells: visual?.cells ?? [],
              openings: visual?.openings ?? [],
              light,
              faces: visual?.faces ?? [],
              exists: true,
            })
            const saved = await updateScene3dScene(projectId, id, payload)
            if (disposedRef.current) return
            const layout = normalizeSceneLayout(saved.scene_state)
            setPlatformLayout(layout)
          } catch {
            // ignore light persist errors (drag spam)
          }
        })()
      }, 400)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ensurePrevizDraftRef is a stable mutable ref
    [captureCurrentSnapshot, projectId, pushHistorySnapshot],
  )
  const handleLightChangeRef = useRef(handleLightChange)
  handleLightChangeRef.current = handleLightChange

  const handlePlatformCommit = useCallback(
    (args: {
      layout: SceneLayoutState
      historyLabelKey: string
      historyLabelParams?: Record<string, string | number>
    }) => {
      const { layout, historyLabelKey, historyLabelParams } = args
      const nextVisual: PlatformLayerState = {
        platform: platformIsSet(layout.platform) ? layout.platform : null,
        cells: layout.cells ?? [],
        faces: layout.faces ?? [],
        openings: layout.openings ?? [],
        selectedSectors: platformVisualRef.current?.selectedSectors ?? [],
        selectedFaceTiles: platformVisualRef.current?.selectedFaceTiles ?? [],
        activeFaceKey: platformVisualRef.current?.activeFaceKey ?? null,
        light: layout.light ?? defaultSceneLight(),
        surfaceGrid: platformVisualRef.current?.surfaceGrid ?? false,
      }
      platformVisualRef.current = nextVisual
      engineRef.current?.setPlatformState(nextVisual)
      setPlatformLayout(layout)
      dirtyRef.current = true
      existsRef.current = true
      setExists(true)
      // Face media lives on the server; undo would only flip local URLs until poll restores them.
      if (!FACE_MEDIA_HISTORY_LABEL_KEYS.has(historyLabelKey)) {
        pushHistorySnapshot(
          historyLabelKey,
          snapshotFromVisual({
            platform: nextVisual.platform,
            cells: nextVisual.cells,
            openings: nextVisual.openings,
            light: nextVisual.light,
            objects: engineRef.current?.getSceneState().objects ?? layout.objects ?? [],
            camera_path: engineRef.current?.getSceneState().camera_path ?? emptyCameraPath(),
            faces: nextVisual.faces?.map((face) => ({
              face_key: face.face_key,
              url: face.url,
              download_url: face.download_url,
              prompt: face.prompt,
              solid_color: face.solid_color,
              tile_colors: face.tile_colors,
            })),
          }),
          historyLabelParams,
        )
      }
      queuePersist()
    },
    [pushHistorySnapshot, queuePersist],
  )

  // Track selected object label on stage for the Rotate/Scale stack.
  useEffect(() => {
    const showChrome =
      selectedUids.length > 0 && canEdit && !deviceCameraActive && !recording && !playing && !pathEdit.lookThrough
    if (!showChrome) {
      setGizmoAnchor(null)
      return
    }
    let raf = 0
    const tick = () => {
      const next = engineRef.current?.getSelectionGizmoAnchorScreen() ?? null
      setGizmoAnchor((prev) => {
        if (prev == null && next == null) return prev
        if (prev && next && Math.abs(prev.x - next.x) < 0.5 && Math.abs(prev.y - next.y) < 0.5) return prev
        return next
      })
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [selectedUids, canEdit, deviceCameraActive, recording, playing, pathEdit.lookThrough])

  // Light marker intensity slider anchor (same chrome as object gizmo stack).
  useEffect(() => {
    const show = lightSelected && canEdit && !deviceCameraActive && !recording && !playing && !pathEdit.lookThrough
    if (!show) {
      setLightAnchor(null)
      return
    }
    let raf = 0
    const tick = () => {
      const eng = engineRef.current
      if (eng) {
        const range = eng.getLightIntensityRange()
        setLightIntensityRange((prev) =>
          Math.abs(prev.min - range.min) < 1e-6 && Math.abs(prev.max - range.max) < 1e-6
            ? prev
            : { min: range.min, max: range.max },
        )
        const intensity = eng.getLightIntensity()
        if (!lightIntensityDraggingRef.current) {
          setLightIntensity((prev) => (Math.abs(prev - intensity) < 1e-4 ? prev : intensity))
        }
        const next = eng.getLightGizmoAnchorScreen()
        setLightAnchor((prev) => {
          if (prev == null && next == null) return prev
          if (prev && next && Math.abs(prev.x - next.x) < 0.5 && Math.abs(prev.y - next.y) < 0.5) return prev
          return next
        })
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [lightSelected, canEdit, deviceCameraActive, recording, playing, pathEdit.lookThrough])

  // Extrude/intrude arrows beside platform selection (sector side / face top).
  useEffect(() => {
    const show =
      Boolean(selectionExtrudeUi?.visible) &&
      canEdit &&
      !deviceCameraActive &&
      !recording &&
      !playing &&
      !pathEdit.lookThrough
    if (!show) {
      setSelectionExtrudeAnchor(null)
      return
    }
    let raf = 0
    const tick = () => {
      const next = engineRef.current?.getPlatformSelectionToolbarAnchorScreen() ?? null
      setSelectionExtrudeAnchor((prev) => {
        if (prev == null && next == null) return prev
        if (
          prev &&
          next &&
          prev.kind === next.kind &&
          prev.side === next.side &&
          prev.faceArrowFlip === next.faceArrowFlip &&
          Math.abs(prev.x - next.x) < 0.5 &&
          Math.abs(prev.y - next.y) < 0.5
        ) {
          return prev
        }
        return next
      })
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [selectionExtrudeUi?.visible, canEdit, deviceCameraActive, recording, playing, pathEdit.lookThrough])

  useLayoutEffect(() => {
    return () => {
      const engine = engineRef.current
      if (engine?.isExporting()) engine.detachExport()
    }
  }, [])

  useEffect(() => {
    if (!portalReady) return
    const stage = stageRef.current
    if (!stage) return

    disposedRef.current = false
    const engine = createScene3dEngine({
      stage,
      onObjectsChange: (next) => {
        if (disposedRef.current) return
        setObjects(next)
        setPlatformLayout((prev) => (prev ? { ...prev, objects: next, exists: true } : prev))
        const visual = platformVisualRef.current
        pushHistorySnapshotRef.current(
          "locationsPage.scene3d.historyActions.objects",
          snapshotFromVisual({
            platform: visual?.platform ?? null,
            cells: visual?.cells ?? [],
            openings: visual?.openings ?? [],
            light: visual?.light ?? null,
            objects: next,
            camera_path: engineRef.current?.getSceneState().camera_path ?? emptyCameraPath(),
            faces: visual?.faces,
          }),
        )
        if (!skipHistoryRef.current) persistObjectsNow()
      },
      onSelectionChange: setSelectedUids,
      onLightSelectionChange: setLightSelected,
      onPlatformSectors: (sectors, additive, opts) => platformInputRef.current.sectors(sectors, additive, opts),
      onPlatformFace: (faceKey, additive) => platformInputRef.current.face(faceKey, additive),
      onPlatformFaceTiles: (tiles, additive) => platformInputRef.current.faceTiles(tiles, additive),
      onPlatformClear: () => {
        const visual = platformVisualRef.current
        if (visual) {
          platformVisualRef.current = {
            ...visual,
            selectedSectors: [],
            selectedFaceTiles: [],
            activeFaceKey: null,
          }
        }
        platformInputRef.current.clear()
      },
      onPlatformToolCleared: () => {
        platformToolRef.current = null
        engineRef.current?.setPlatformTool(null)
        platformClearToolRef.current()
      },
      onPlatformToolActivated: (tool) => {
        platformToolRef.current = tool
        engineRef.current?.setPlatformTool(tool)
        platformActivateToolRef.current(tool)
      },
      onLightChange: (light) => handleLightChangeRef.current(light),
      onCameraPathChange: (path, opts) => {
        const startUndo = placeStartUndoRef.current
        placeStartUndoRef.current = null
        if (disposedRef.current) return
        const recording = engineRef.current?.isRecording() ?? false
        setCameraPath(path)
        setPlatformLayout((prev) => (prev ? { ...prev, camera_path: path, exists: true } : prev))
        if (opts?.clearRedo) clearHistoryRedoRef.current()
        if (opts?.pendingFit) return
        if (hasCameraPath(path)) {
          if (!skipHistoryRef.current) syncPathExportDirtyRef.current(true)
        } else {
          syncPathExportDirtyRef.current(false)
        }
        if (!recording) {
          const prev = pathHistoryPrevRef.current
          const skipHistory = skipHistoryRef.current || opts?.skipHistory
          const pathChanged = prev != null && !cameraPathsEqual(prev, path)
          if (prev && !skipHistory && (pathChanged || startUndo)) {
            const visual = platformVisualRef.current
            const state = engineRef.current?.getSceneState()
            pushHistorySnapshotRef.current(pathEditHistoryLabelKey(prev, path, startUndo != null), {
              ...snapshotFromVisual({
                platform: visual?.platform ?? null,
                cells: visual?.cells ?? [],
                openings: visual?.openings ?? [],
                light: visual?.light ?? null,
                objects: state?.objects ?? [],
                camera_path: path,
                faces: visual?.faces,
              }),
              prior_camera_path: cloneCameraPath(prev),
              ...(startUndo
                ? {
                    replaces_start_camera: true,
                    prior_start_camera: startUndo.prior,
                    start_camera: cloneStartCamera(startCameraRef.current),
                  }
                : {}),
            })
          }
          pathHistoryPrevRef.current = cloneCameraPath(path)
        }
        queuePersist()
      },
      onPathEditUi: (ui) => {
        if (disposedRef.current) return
        setPathEdit(ui)
      },
      onCameraPoseCommit: () => {
        if (disposedRef.current) return
        queuePersist()
      },
      onPlaybackChange: (next) => {
        if (disposedRef.current) return
        setPlaying(next)
        if (next) {
          setViewPlaybackEnded(false)
          return
        }
        if (cameraViewActiveRef.current && engineRef.current?.isPathAtEnd()) setViewPlaybackEnded(true)
      },
      onRecordingChange: setRecording,
      onRecordArmChange: setRecordArmed,
      cameraPathTakeName: (n) => cameraPathTakeNameRef.current(n),
      onCameraPathTakesFull: () => cameraPathTakesFullRef.current(),
      onCameraPlaced: (cam) => {
        if (disposedRef.current) return
        placeStartUndoRef.current = { prior: cloneStartCamera(startCameraRef.current) }
        startCameraRef.current = cam
        setHasStartCamera(true)
      },
      onCameraPlacePreview: (active) => {
        if (disposedRef.current) return
        setPlacePreview(active)
      },
      onCameraPlaceModeChange: (active) => {
        if (disposedRef.current) return
        setCameraPlaceMode(active)
      },
      onRecordOverwriteNeeded: () => setOverwriteRecordOpen(true),
      canRecord: canEdit,
      canEdit,
      frameFormat: frame,
      onViewOrientation: (direction, up, matched) => {
        const cube = viewCubeRef.current
        if (!cube) return
        cube.setOrientation(direction, up)
        if (viewCubeDragRef.current || engineRef.current?.isViewAnimating()) return
        cube.setActiveView(matched)
      },
      onPathTimeChange: (ms) => pathTimeListenerRef.current(ms),
      onRecordTick: (ms) => {
        overlayRecordTickRef.current(ms)
      },
    })
    engineRef.current = engine
    setSceneBooted(false)

    let cancelled = false
    const applyLoadedLayout = (layout: ReturnType<typeof normalizeSceneLayout>) => {
      if (cancelled || disposedRef.current) return
      existsRef.current = layout.exists
      setExists(layout.exists)
      if (layout.exists || sceneMode === "editPreviz" || scene3dId != null) {
        autosaveEnabledRef.current = true
        setAutosaveEnabled(true)
      }
      dirtyRef.current = false
      startCameraRef.current = layout.start_camera
      setHasStartCamera(layout.start_camera != null)
      // Load emits onObjectsChange before visual is ready — skip so undo doesn't restore null platform/light.
      skipHistoryRef.current = true
      try {
        const platformVisual: PlatformLayerState = {
          platform: platformIsSet(layout.platform) ? layout.platform : null,
          cells: layout.cells ?? [],
          faces: layout.faces ?? [],
          openings: layout.openings ?? [],
          selectedSectors: [],
          selectedFaceTiles: [],
          activeFaceKey: null,
          light: normalizeSceneLight(layout.light),
          surfaceGrid: false,
        }
        platformVisualRef.current = platformVisual
        engine.applySceneState({ ...layout, camera_state: layout.camera_state })
        engine.setCameraPlaceAim(
          cameraPlaceAimFromStart(layout.start_camera, layout.camera_path.authoring?.points.length ?? 0),
        )
        engine.setPlatformState(platformVisual)
        engine.setPlatformTool(canEdit ? platformToolRef.current : null)
        setFov(Math.round(engine.getFov()))
        setEnvironment(normalizeSceneEnvironment(layout.environment))
        setPlatformLayout(layout)
        setObjects(layout.objects)
        setCameraPath(layout.camera_path)
        syncPathExportDirtyRef.current(hasCameraPath(layout.camera_path) && !hasVideoRef.current)
        engine.setNavMode("fly")
        if (sceneMode === "newPreviz" && canEdit && presetCharacterIds && presetCharacterIds.length > 0) {
          const placed = new Set(layout.objects.filter((o) => o.entity_type === "character").map((o) => o.entity_id))
          const terrain = platformIsSet(platformVisual.platform)
            ? { platform: platformVisual.platform, cells: platformVisual.cells }
            : null
          for (const characterId of presetCharacterIds) {
            if (placed.has(characterId)) continue
            const character = cast.find((c) => c.id === characterId)
            if (!character) continue
            engine.addObject(
              buildCharacterObject(
                character,
                engine.getSceneState().objects,
                cast,
                props,
                terrain,
                engine.dropViewHint(),
              ),
            )
          }
        }
      } finally {
        skipHistoryRef.current = false
      }
      if (sceneMode === "newPreviz") {
        clearAndReloadHistory()
      }
      // Seed baseline so the first user action can be undone (needs length >= 2).
      loadOrSeedBaseline(() => captureCurrentSnapshot())
      setSceneBooted(true)
    }

    if (sceneMode === "newPreviz") {
      const opening = openingCameraPathTake(t("locationsPage.scene3d.timeline.takeName", { n: 1 }))
      applyLoadedLayout({
        ...defaultPrevizLayout(),
        camera_path: opening.path,
        camera_paths: [opening],
        active_camera_path_id: opening.id,
      })
    } else {
      const layout = ensurePrevizPlatform(normalizeSceneLayout(initialSceneState))
      const withExists = {
        ...layout,
        exists:
          layout.objects.length > 0 ||
          hasCameraPath(layout.camera_path) ||
          layout.start_camera != null ||
          platformIsSet(layout.platform),
      }
      applyLoadedLayout(withExists)
    }

    return () => {
      cancelled = true
      disposedRef.current = true
      setSceneBooted(false)
      deviceLookRef.current?.stop()
      deviceLookRef.current = null
      clearPersistTimer()
      if (persistLightTimerRef.current != null) {
        window.clearTimeout(persistLightTimerRef.current)
        persistLightTimerRef.current = null
      }
      if (canEdit && dirtyRef.current) {
        const id = boundScene3dIdRef.current ?? scene3dId
        if (id != null) {
          const snapshot = engine.getSceneState()
          const visual = platformVisualRef.current
          dirtyRef.current = false
          const payload = sceneStateForSave({
            ...emptySceneLayout(),
            ...snapshot,
            start_camera: startCameraRef.current,
            platform: visual?.platform ?? null,
            cells: visual?.cells ?? [],
            openings: visual?.openings ?? [],
            light: visual?.light ?? null,
            ...(visual ? { faces: visual.faces ?? [] } : {}),
            exists: true,
          })
          void updateScene3dScene(projectId, id, payload)
            .then(() => onLayoutChanged?.())
            .catch(() => {})
        }
      }
      if (engine.isExporting()) engine.detachExport()
      else engine.destroy()
      engineRef.current = null
    }
    // Engine is bound to this mount; persist/load identity is project/location/mode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, locationId, portalReady, sceneMode, previzId])

  useEffect(() => {
    engineRef.current?.setPlatformTool(canEdit ? platformToolRef.current : null)
  }, [canEdit, sceneBooted])

  useLocationSceneFacePoll({
    hasPendingFaces,
    projectId,
    scene3dId,
    boundScene3dIdRef,
    engineRef,
    platformVisualRef,
    platformLayoutRef,
    setPlatformLayout,
  })

  const addPrimitive = useCallback(
    (shape: ScenePrimitiveShape) => {
      const engine = engineRef.current
      if (!engine || !canEdit) return
      const visual = platformVisualRef.current
      const terrain =
        visual?.platform && platformIsSet(visual.platform) ? { platform: visual.platform, cells: visual.cells } : null
      engine.addObject(buildPrimitiveObject(shape, engine.getSceneState().objects, terrain, engine.dropViewHint()))
    },
    [canEdit],
  )

  const addCharacter = useCallback(
    (character: CastRow) => {
      const engine = engineRef.current
      if (!engine || !canEdit) return
      const visual = platformVisualRef.current
      const terrain =
        visual?.platform && platformIsSet(visual.platform) ? { platform: visual.platform, cells: visual.cells } : null
      engine.addObject(
        buildCharacterObject(character, engine.getSceneState().objects, cast, props, terrain, engine.dropViewHint()),
      )
    },
    [canEdit, cast, props],
  )

  const addProp = useCallback(
    (prop: PropRow) => {
      const engine = engineRef.current
      if (!engine || !canEdit) return
      const visual = platformVisualRef.current
      const terrain =
        visual?.platform && platformIsSet(visual.platform) ? { platform: visual.platform, cells: visual.cells } : null
      engine.addObject(
        buildPropObject(prop, engine.getSceneState().objects, cast, props, terrain, engine.dropViewHint()),
      )
    },
    [canEdit, cast, props],
  )

  const handlePickSceneEntity = useCallback(
    (ref: { entity_type?: string | null; entity_id?: number | string | null }) => {
      const id = typeof ref.entity_id === "string" ? Number(ref.entity_id) : ref.entity_id
      if (id == null || Number.isNaN(id)) return
      if (ref.entity_type === "character") {
        const character = cast.find((c) => c.id === id)
        if (character) addCharacter(character)
        return
      }
      if (ref.entity_type === "prop") {
        const prop = props.find((p) => p.id === id)
        if (prop) addProp(prop)
      }
    },
    [addCharacter, addProp, cast, props],
  )

  const handleReset = async () => {
    try {
      const opening = openingCameraPathTake(t("locationsPage.scene3d.timeline.takeName", { n: 1 }))
      const resetLayout = {
        ...defaultPrevizLayout(),
        camera_path: opening.path,
        camera_paths: [opening],
        active_camera_path_id: opening.id,
      }
      const empty = sceneStateForSave({ ...resetLayout, faces: [] })
      const id =
        sceneMode === "editPreviz"
          ? (boundScene3dIdRef.current ?? scene3dId)
          : sceneMode === "newPreviz"
            ? boundScene3dIdRef.current
            : null
      const faceKeys = Array.from(
        new Set(
          [
            ...(platformVisualRef.current?.faces ?? []).map((face) => face.face_key),
            ...(platformLayoutRef.current?.faces ?? []).map((face) => face.face_key),
          ].filter((key): key is string => Boolean(key)),
        ),
      )
      if (id != null && faceKeys.length > 0) {
        await remapSceneFaces(projectId, id, [], faceKeys)
      }
      if (id != null) {
        await updateScene3dScene(projectId, id, empty)
      }
      existsRef.current = false
      setExists(false)
      dirtyRef.current = false
      startCameraRef.current = null
      setHasStartCamera(false)
      skipHistoryRef.current = true
      try {
        const cleared: PlatformLayerState = {
          platform: emptyPlatform(),
          cells: [],
          faces: [],
          openings: [],
          selectedSectors: [],
          selectedFaceTiles: [],
          activeFaceKey: null,
          light: defaultSceneLight(),
          surfaceGrid: false,
        }
        platformVisualRef.current = cleared
        engineRef.current?.applySceneState(resetLayout)
        engineRef.current?.setCameraPlaceAim(null)
        engineRef.current?.setPlatformState(cleared)
        setFov(Math.round(engineRef.current?.getFov() ?? 55))
        setEnvironment(normalizeSceneEnvironment(resetLayout.environment))
        setPlatformLayout(resetLayout)
        setObjects([])
        setCameraPath(resetLayout.camera_path)
        resetHistoryToBaseline(captureCurrentSnapshot())
      } finally {
        skipHistoryRef.current = false
      }
      onLayoutChanged?.()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e))
    }
    setResetOpen(false)
  }

  const saveStartCamera = useCallback(async () => {
    const engine = engineRef.current
    if (!engine || !canEdit) return
    const cam = { ...engine.getSceneState().camera_state }
    startCameraRef.current = cam
    setHasStartCamera(true)
    dirtyRef.current = true
    const visual = platformVisualRef.current
    const payload = sceneStateForSave({
      ...emptySceneLayout(),
      ...engine.getSceneState(),
      start_camera: cam,
      platform: visual?.platform ?? null,
      cells: visual?.cells ?? [],
      openings: visual?.openings ?? [],
      light: visual?.light ?? null,
      ...(visual ? { faces: visual.faces ?? [] } : {}),
      exists: true,
    })
    try {
      const id = await ensurePrevizDraft({ force: true })
      if (id == null) return
      await updateScene3dScene(projectId, id, payload)
      dirtyRef.current = false
      existsRef.current = true
      setExists(true)
      onLayoutChanged?.()
      toast.success(t("locationsPage.scene3d.startCameraSaved"))
    } catch (e) {
      dirtyRef.current = true
      toast.error(e instanceof Error ? e.message : String(e))
    }
  }, [canEdit, ensurePrevizDraft, onLayoutChanged, projectId, t])

  const goToStartCamera = useCallback(async () => {
    const engine = engineRef.current
    if (!engine || goToStartBusy) return
    setGoToStartBusy(true)
    try {
      const cam = startCameraRef.current
      if (!cam) {
        startCameraRef.current = null
        setHasStartCamera(false)
        toast.error(t("locationsPage.scene3d.startCameraMissing"))
        return
      }
      if (deviceCameraActive) stopDeviceCamera()
      const keepMode = engine.getNavMode()
      engine.animateToCameraState({ ...cam, mode: keepMode }, () => {
        if (disposedRef.current || engineRef.current !== engine) return
        setFov(Math.round(engine.getFov()))
      })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e))
    } finally {
      setGoToStartBusy(false)
    }
  }, [deviceCameraActive, goToStartBusy, stopDeviceCamera, t])

  const captureViewpoint = useCallback(async () => {
    if (!canEdit || photoBusy) return
    setPhotoBusy(true)
    try {
      await saveViewpointStill()
    } finally {
      setPhotoBusy(false)
    }
  }, [canEdit, photoBusy, saveViewpointStill])

  const viewCubeLabels = useMemo(() => ({ top: "Top", front: "Front", back: "Back", left: "Left", right: "Right" }), [])

  const onMoveKeyPress = useCallback((code: MoveKeyCode) => {
    engineRef.current?.setFlyHeld(MOVE_KEY_AXIS[code], true)
  }, [])

  const onMoveKeyRelease = useCallback((code: MoveKeyCode) => {
    engineRef.current?.setFlyHeld(MOVE_KEY_AXIS[code], false)
  }, [])

  const dismissCaptureHints = useCallback(() => {
    if (scene3dId != null) markCaptureHintSeen(scene3dId)
    setCaptureHintStep(null)
  }, [scene3dId])

  const onOverlaySnapshot = useCallback(() => {
    dismissCaptureHints()
    if (!canEdit || photoBusy || previzExporting) return
    setFlashKey((key) => key + 1)
    void captureViewpoint()
  }, [canEdit, captureViewpoint, dismissCaptureHints, photoBusy, previzExporting])
  captureViewpointActionRef.current = onOverlaySnapshot
  toggleObjectPickerRef.current = () => openPickerFrom("assistant")

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target
      if (target instanceof HTMLElement) {
        if (target.isContentEditable) return
        const tag = target.tagName
        if (tag === "TEXTAREA" || tag === "SELECT") return
        if (tag === "INPUT") {
          const type = (target as HTMLInputElement).type
          if (type !== "range" && type !== "checkbox" && type !== "radio" && type !== "button" && type !== "submit") {
            return
          }
        }
      }
      const mod = event.metaKey || event.ctrlKey
      if (mod && event.code === "KeyZ" && !event.altKey) {
        if (!canEdit) return
        event.preventDefault()
        if (event.shiftKey) redoLastAction()
        else undoLastAction()
        return
      }
      if (event.code === "KeyY" && event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey) {
        if (!canEdit) return
        event.preventDefault()
        redoLastAction()
        return
      }
      if (event.code !== "KeyP" || event.repeat) return
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (!canEdit || photoBusy || previzExporting) return
      event.preventDefault()
      onOverlaySnapshot()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [canEdit, onOverlaySnapshot, photoBusy, previzExporting, redoLastAction, undoLastAction])

  const recordButtonLabel = recording
    ? t("locationsPage.scene3d.stopRecord")
    : recordArmed
      ? t("locationsPage.scene3d.recordCancel")
      : t("locationsPage.scene3d.record")
  const recordArmedHint = t("locationsPage.scene3d.recordArmedHint")
  const capturePhase: CapturePhase = recording ? "recording" : recordArmed ? "armed" : "idle"
  const overlayRecordLabel =
    capturePhase === "recording"
      ? t("locationsPage.scene3d.overlay.recordStop")
      : capturePhase === "armed"
        ? recordArmedHint
        : t("locationsPage.scene3d.overlay.recordStart")
  const moveKeyHints = {
    KeyW: t("locationsPage.scene3d.overlay.keyW"),
    KeyS: t("locationsPage.scene3d.overlay.keyS"),
    KeyA: t("locationsPage.scene3d.overlay.keyA"),
    KeyD: t("locationsPage.scene3d.overlay.keyD"),
    KeyR: t("locationsPage.scene3d.overlay.keyR"),
    KeyF: t("locationsPage.scene3d.overlay.keyF"),
  }
  const phoneBooting = canEdit && (autoStartDeviceCamera || autoStartCameraView) && !deviceCameraActive
  const showSceneControls = !phoneLayout && !deviceCameraActive && !previzExporting && !phoneBooting
  const showPlaceMonitor =
    cameraPlaceMode && cameraPlaceMonitorVisible(placePreview, cameraPath.authoring?.points.length ?? 0)

  useEffect(() => {
    const engine = engineRef.current
    if (!engine) return
    engine.setCameraPlaceMonitor(showPlaceMonitor && showSceneControls ? placeMonitorRef.current : null)
    return () => engine.setCameraPlaceMonitor(null)
  }, [showPlaceMonitor, sceneBooted, showSceneControls])

  useEffect(() => {
    if (!captureHints || scene3dId == null || !showSceneControls || captureHintSeen(scene3dId)) {
      setCaptureHintStep(null)
      return
    }
    // Сначала фото, потом запись, затем позиционирование. Клик помечает сцену просмотренной и гасит хвост таймеров.
    const showSnapshot = window.setTimeout(() => {
      if (captureHintSeen(scene3dId)) return
      setCaptureHintStep("snapshot")
    }, 0)
    const showRecord = window.setTimeout(() => {
      if (captureHintSeen(scene3dId)) return
      setCaptureHintStep("record")
    }, CAPTURE_HINT_MS)
    const showPlace = window.setTimeout(() => {
      if (captureHintSeen(scene3dId)) return
      setCaptureHintStep("place")
    }, CAPTURE_HINT_MS * 2)
    const done = window.setTimeout(() => {
      if (captureHintSeen(scene3dId)) return
      markCaptureHintSeen(scene3dId)
      setCaptureHintStep(null)
    }, CAPTURE_HINT_MS * 3)
    return () => {
      window.clearTimeout(showSnapshot)
      window.clearTimeout(showRecord)
      window.clearTimeout(showPlace)
      window.clearTimeout(done)
    }
  }, [captureHints, scene3dId, showSceneControls])

  useEffect(() => {
    if (!recording) return
    setOverlayElapsedMs(0)
    recordElapsedRef.current = 0
    overlayPaintAtRef.current = 0
  }, [recording])

  const onOverlayRecord = useCallback(() => {
    dismissCaptureHints()
    if (recording || recordArmed) {
      engineRef.current?.stopRecording({ returnHome: true })
      return
    }
    engineRef.current?.startRecording()
  }, [dismissCaptureHints, recordArmed, recording])

  const pickerExistingKeys = useMemo(() => {
    const keys: string[] = []
    for (const o of objects) {
      if ((o.kind !== "character" && o.kind !== "prop") || o.entity_id == null) continue
      const type = o.entity_type ?? o.kind
      keys.push(`${type}:${o.entity_id}`)
    }
    return keys
  }, [objects])

  // Host z: WorkspaceFullscreenPanel 101 / nested 111|113|131; phone-fly above agent/shell.
  const immersiveStage = deviceCameraActive
  const stageZ = immersiveStage ? DEVICE_FLY_STAGE_Z : nested === 3 ? 132 : nested === 2 ? 114 : nested ? 112 : 102
  const pickerPortalZ = stageZ + 1
  const flyUrl = useMemo(() => {
    const id = boundScene3dId ?? scene3dId
    if (id == null) return null
    return scene3dWorkspaceFlyUrl(projectId, id, { shotId })
  }, [boundScene3dId, projectId, scene3dId, shotId])
  const viewUrl = useMemo(() => {
    const id = boundScene3dId ?? scene3dId
    if (id == null) return null
    return scene3dWorkspaceViewUrl(projectId, id, { shotId })
  }, [boundScene3dId, projectId, scene3dId, shotId])
  const flyQrLabel = t("locationsPage.scene3d.flyQrLabel")
  const viewQrLabel = t("locationsPage.scene3d.viewQrLabel")
  const showFlyQr = false
  const showViewQr = viewUrl != null && !deviceCameraActive && canEdit && !phoneLayout

  const prepareFlyQr = useCallback(async (): Promise<boolean> => {
    setFlyQrBusy(true)
    try {
      const id = await ensurePrevizDraft({ force: true })
      return id != null
    } finally {
      setFlyQrBusy(false)
    }
  }, [ensurePrevizDraft])

  const commitSceneName = useCallback(
    (name: string) => {
      const id = boundScene3dId ?? scene3dId
      if (id == null) return
      const previous = shownSceneName
      const saveId = sceneNameSaveRef.current + 1
      sceneNameSaveRef.current = saveId
      setShownSceneName(name)
      void (async () => {
        try {
          await renameScene3d(projectId, id, name)
          if (sceneNameSaveRef.current !== saveId) return
          sceneNameSaveRef.current = 0
          onSceneNameSaved?.(name)
        } catch (e) {
          if (sceneNameSaveRef.current !== saveId) return
          sceneNameSaveRef.current = 0
          setShownSceneName(previous)
          toast.error(e instanceof Error ? e.message : String(e))
        }
      })()
    },
    [boundScene3dId, onSceneNameSaved, projectId, scene3dId, shownSceneName],
  )

  const observeUid = pathEdit.look.mode === "observe" ? pathEdit.look.object_uid : ""
  const observeObject = observeUid ? objects.find((object) => object.uid === observeUid) : undefined
  const observeTargetLabel = !observeObject
    ? null
    : observeObject.label?.trim()
      ? observeObject.label
      : observeObject.kind === "primitive"
        ? t("locationsPage.scene3d.trajectory.figure")
        : null
  const observePrimitive = observeObject?.kind === "primitive"
  const observeThumb = observeObjectThumb(observeObject, cast, props)

  const cameraPlaceBusy = recording || recordArmed || playing || previzExporting

  const confirmCameraPlaceTake = useCallback(() => {
    const engine = engineRef.current
    if (!engine || cameraPlaceBusy) return
    if (cameraPlaceTakeIsEmpty(engine.getCameraPath())) {
      engine.setCameraPlaceMode(true)
      return
    }
    const takes = engine.getCameraPathTakes().takes
    if (cameraPathTakesFull(takes)) {
      toast.error(t("locationsPage.scene3d.timeline.takesFull"))
      return
    }
    const id = engine.addCameraPathTake(cameraPathTakeNameRef.current(nextCameraPathTakeNumber(takes)))
    if (!id) return
    engine.setCameraPlaceMode(true)
  }, [cameraPlaceBusy, t])

  const toolbar = !deviceCameraActive ? (
    <LocationScene3dToolbar
      canEdit={canEdit}
      showSaveStart={false}
      showGoToStart={false}
      goToStartBusy={goToStartBusy}
      canGoToStart={hasStartCamera}
      onSaveStart={() => void saveStartCamera()}
      onGoToStart={() => void goToStartCamera()}
      canUndo={historyEntries.length >= 2}
      onUndo={undoLastAction}
      canRedo={redoStack.length > 0}
      onRedo={redoLastAction}
      historyEntries={historyEntries}
      historyOpen={historyOpen}
      onHistoryOpenChange={(open) => {
        if (open) setPickerOpen(false)
        setHistoryOpen(open)
      }}
      onClearHistory={() => {
        clearAndReloadHistory()
      }}
      onRestoreHistory={rewindHistoryTo}
      lightPreset={lightPreset}
      lightMenuOpen={lightMenuOpen}
      onLightMenuOpenChange={(open) => {
        if (open) setPickerOpen(false)
        setLightMenuOpen(open)
      }}
      onApplyLightPreset={(side) => {
        lightPresetsRef.current?.apply(side)
        setLightMenuOpen(false)
      }}
      platformDraft={platformFormChrome?.draft ?? null}
      onPlatformDraftChange={(patch) => platformFormApiRef.current?.setDraft(patch)}
      onPlatformApply={(patch) => platformFormApiRef.current?.apply(patch)}
      platformBusy={platformFormChrome?.busy ?? false}
      flySpeed={flySpeed}
      onFlySpeedChange={(v) => {
        setFlySpeed(v)
        engineRef.current?.setFlySpeed(v)
      }}
      fov={fov}
      onFovChange={(v) => {
        setFov(v)
        engineRef.current?.setFov(v)
        queuePersist()
      }}
      showFlyQr={Boolean(showFlyQr && flyUrl)}
      flyUrl={flyUrl ?? ""}
      flyQrLabel={flyQrLabel}
      onFlyQrBeforeOpen={prepareFlyQr}
      flyQrBusy={flyQrBusy}
      showViewQr={Boolean(showViewQr && viewUrl)}
      viewUrl={viewUrl ?? ""}
      viewQrLabel={viewQrLabel}
      phoneLayout={phoneLayout}
      onEnterPhoneFly={() => enterDeviceCameraUi("fly")}
      onRequestClose={onRequestClose}
      trajectoryActive={pathEdit.active}
      trajectoryPanel={
        pathEdit.active && canEdit && !phoneLayout ? (
          <LocationScenePathEditPanel
            ui={pathEdit}
            targetLabel={observeTargetLabel}
            targetThumb={observeThumb}
            targetPrimitive={observePrimitive}
            hasPath={hasCameraPath(cameraPath)}
            playing={playing}
            onDraw={(draw) => engineRef.current?.setPathDraw(draw)}
            onLook={(look) => engineRef.current?.setPathLook(look)}
            onPickObject={() => engineRef.current?.armObservePick()}
            onArmLookRewrite={() => engineRef.current?.armLookRewrite()}
            onCancelLook={() => engineRef.current?.cancelLookPass()}
            onGoToPathStart={() => {
              const engine = engineRef.current
              if (!engine) return
              engine.placeAtPathStart()
              engine.setPathLookThrough(true)
            }}
          />
        ) : null
      }
      onToggleTrajectory={
        canEdit
          ? () => {
              const engine = engineRef.current
              if (!engine) return
              engine.setTrajectoryMode(!engine.isTrajectoryMode())
            }
          : undefined
      }
      showCameraPlace={canEdit && !phoneLayout}
      cameraPlaceActive={cameraPlaceMode}
      cameraPlaceDisabled={cameraPlaceBusy}
      cameraPlaceHint={captureHintStep === "place" ? t("locationsPage.scene3d.captureHintPlace") : undefined}
      onToggleCameraPlace={() => {
        dismissCaptureHints()
        const engine = engineRef.current
        if (!engine) return
        if (engine.isCameraPlaceMode()) {
          engine.setCameraPlaceMode(false)
          return
        }
        if (cameraPlaceBusy) return
        if (cameraPlaceTakeIsEmpty(engine.getCameraPath())) {
          engine.setCameraPlaceMode(true)
          return
        }
        setCameraPlaceConfirmOpen(true)
      }}
      onLeaveCameraPlace={() => {
        const engine = engineRef.current
        if (!engine?.isCameraPlaceMode()) return
        engine.setCameraPlaceMode(false)
      }}
      addButtonRef={addButtonRef}
      pickerOpen={pickerOpen}
      pickerToolbarActive={pickerOpen && pickerFrom === "toolbar"}
      onTogglePicker={() => openPickerFrom("toolbar")}
      onDismissPicker={() => setPickerOpen(false)}
      sceneName={shownSceneName}
      onSceneNameCommit={commitSceneName}
      skyMarkup={environment.sky_markup}
      onSkyMarkupChange={(next) => {
        const environmentNext = { ...environment, sky_markup: next }
        setEnvironment(environmentNext)
        engineRef.current?.applyEnvironment(environmentNext)
        setPlatformLayout((prev) => (prev ? { ...prev, environment: environmentNext, exists: true } : prev))
        queuePersist()
      }}
    />
  ) : null

  const assistantAnchor =
    pickerOpen && pickerFrom === "assistant" && assistantPickerAnchorRef ? assistantPickerAnchorRef : null
  const addPicker =
    canEdit && !deviceCameraActive ? (
      <ReferencePickerDrawer
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        placement={{ mode: "anchored", direction: assistantAnchor ? "up" : "down" }}
        outsideClickIgnoreRefs={[addButtonRef, ...(pickerIgnoreRefs ?? [])]}
        anchorRef={assistantAnchor ?? addButtonRef}
        characters={cast}
        locations={[]}
        props={props}
        existingKeys={pickerExistingKeys}
        tabs={["character", "prop", "primitive"]}
        entityPick
        closeOnDialogBackgroundClick
        portalZIndex={pickerPortalZ}
        title={t("locationsPage.scene3d.pickerTitle")}
        subtitle={t("locationsPage.scene3d.pickerSubtitle")}
        onPickReference={handlePickSceneEntity}
        onPickPrimitive={addPrimitive}
        onPreviewRef={setPickerPreviewRef}
        previewLightboxNested={3}
      />
    ) : null

  const stageVisible = stageBox.width >= 32 && stageBox.height >= 32
  // Phone-fly: fixed visualViewport box. Otherwise: fill in-flow aspect slot (no scroll sync).
  const stageHostStyle: CSSProperties = immersiveStage
    ? {
        position: "fixed",
        top: stageBox.top,
        left: stageBox.left,
        width: stageBox.width,
        height: stageBox.height,
        zIndex: stageZ,
        visibility: stageVisible ? "visible" : "hidden",
        pointerEvents: stageVisible ? "auto" : "none",
      }
    : {
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        zIndex: 1,
        visibility: stageVisible ? "visible" : "hidden",
        pointerEvents: stageVisible && !pickerOpen ? "auto" : "none",
      }

  const frameMaskBackground = environment.theme === "dark" ? "rgb(0 0 0 / 0.35)" : "rgb(0 0 0 / 0.16)"

  const stageSurface = (
    <div
      ref={stageRef}
      {...{ [SCENE_3D_STAGE_ATTR]: "" }}
      className="relative overflow-hidden rounded-none bg-[#d0d1d2] select-none [-webkit-touch-callout:none]"
      style={stageHostStyle}
      onContextMenu={(e) => e.preventDefault()}
    >
      <LocationScenePlatformDialog
        hosted
        embedded
        open
        onOpenChange={() => {}}
        projectId={projectId}
        locationId={locationId}
        locationName={locationName}
        frameFormat={frameFormat}
        modelLabel={modelLabel}
        canEdit={canEdit}
        scene3dId={boundScene3dId ?? scene3dId}
        ensureScene3dId={() => ensurePrevizDraft({ force: true })}
        hostedLayout={platformLayout}
        onBindInput={(handlers) => {
          platformInputRef.current = handlers
        }}
        onBindClearTool={(clear) => {
          platformClearToolRef.current = clear
        }}
        onBindActivateTool={(activate) => {
          platformActivateToolRef.current = activate
        }}
        onBindSelectionExtrude={handleBindSelectionExtrude}
        onBindLightPresets={handleBindLightPresets}
        onBindPlatformForm={handleBindPlatformForm}
        onPlatformCommit={handlePlatformCommit}
        faceToolbarAnchor={
          selectionExtrudeAnchor?.kind === "face" ? { x: selectionExtrudeAnchor.x, y: selectionExtrudeAnchor.y } : null
        }
        onToolChange={(tool) => {
          platformToolRef.current = tool
          engineRef.current?.setPlatformTool(tool)
        }}
        onVisual={handlePlatformVisual}
        onPatchWallTileColors={(tiles, hex) => {
          engineRef.current?.patchWallTileColors(tiles, hex)
          const visual = platformVisualRef.current
          if (!visual?.platform) return
          platformVisualRef.current = {
            ...visual,
            faces: applyWallTileColors(visual.faces, tiles, hex, visual.cells, visual.platform),
          }
        }}
        onPatchSolidFaceColors={(faceKeys, hex) => {
          engineRef.current?.patchSolidFaceColors(faceKeys, hex)
          const visual = platformVisualRef.current
          if (!visual) return
          const keySet = new Set(faceKeys)
          const faces = visual.faces.map((face) =>
            keySet.has(face.face_key) ? { ...face, solid_color: hex, status: "ready" as const } : face,
          )
          for (const faceKey of faceKeys) {
            if (faces.some((face) => face.face_key === faceKey)) continue
            faces.push({ face_key: faceKey, origin: "upload", status: "ready", solid_color: hex })
          }
          platformVisualRef.current = { ...visual, faces }
        }}
        onClearSolidFaceColors={(faceKeys) => {
          engineRef.current?.clearSolidFaceColors(faceKeys)
          const visual = platformVisualRef.current
          if (!visual) return
          const keySet = new Set(faceKeys)
          platformVisualRef.current = {
            ...visual,
            faces: visual.faces.map((face) => (keySet.has(face.face_key) ? { ...face, solid_color: null } : face)),
          }
        }}
        onLightPersist={(light: SceneLightState) => {
          const prev = platformVisualRef.current
          if (prev) platformVisualRef.current = { ...prev, light }
          engineRef.current?.setLightState(light)
        }}
        onLayoutApplied={(next) => {
          setPlatformLayout(next)
          const hasPlatform = platformIsSet(next.platform)
          if (hasPlatform || next.exists) {
            existsRef.current = true
            setExists(true)
          }
          onLayoutChanged?.()
        }}
      />
      {showFrameMask && frameMask ? (
        <div className="pointer-events-none absolute inset-0 z-[4]" aria-hidden>
          <div className="absolute inset-x-0 top-0" style={{ height: frameMask.y, background: frameMaskBackground }} />
          <div
            className="absolute inset-x-0 bottom-0"
            style={{ top: frameMask.y + frameMask.height, background: frameMaskBackground }}
          />
          <div
            className="absolute left-0"
            style={{
              top: frameMask.y,
              width: frameMask.x,
              height: frameMask.height,
              background: frameMaskBackground,
            }}
          />
          <div
            className="absolute right-0"
            style={{
              top: frameMask.y,
              left: frameMask.x + frameMask.width,
              height: frameMask.height,
              background: frameMaskBackground,
            }}
          />
        </div>
      ) : null}
      {!previzExporting ? (
        <div className="pointer-events-none absolute inset-0 z-[5]" aria-hidden>
          <div className="absolute top-1/2 left-1/2 h-px w-4 -translate-x-1/2 -translate-y-1/2 bg-white/40" />
          <div className="absolute top-1/2 left-1/2 h-4 w-px -translate-x-1/2 -translate-y-1/2 bg-white/40" />
        </div>
      ) : null}
      {recordArmed && !previzExporting ? (
        <div
          className={cn(
            "pointer-events-none absolute top-1/2 left-1/2 z-40 -translate-x-1/2 -translate-y-1/2",
            "rounded-md bg-black/55 px-2.5 py-1 text-[11px] whitespace-nowrap text-white/90",
          )}
        >
          {recordArmedHint}
        </div>
      ) : null}
      {pathEdit.lookRewriteArmed && !recordArmed && !previzExporting ? (
        <div
          className={cn(
            "pointer-events-none absolute top-1/2 left-1/2 z-40 -translate-x-1/2 -translate-y-1/2",
            "rounded-md bg-black/55 px-2.5 py-1 text-[11px] whitespace-nowrap text-white/90",
          )}
        >
          {t("locationsPage.scene3d.trajectory.rewriteLookHint")}
        </div>
      ) : null}
      {deviceCameraActive ? (
        <>
          <div
            className="absolute inset-0 z-10 touch-none select-none [-webkit-touch-callout:none]"
            aria-label={t("locationsPage.scene3d.deviceCamera.doubleTapExit")}
            onContextMenu={(e) => e.preventDefault()}
            onPointerDown={onImmersiveStagePointerDown}
          />
          <div
            className={cn(
              "pointer-events-none absolute top-[max(0.75rem,env(safe-area-inset-top))] left-[max(0.75rem,env(safe-area-inset-left))] z-50",
            )}
          >
            <div className="rounded-md bg-red-600/80 px-2 py-0.5 text-[11px] font-medium tracking-wide text-white/95">
              {t("locationsPage.scene3d.liveSync.live")}
            </div>
          </div>
          <button
            type="button"
            className={cn(
              "pointer-events-auto absolute z-50 flex h-8 w-8 items-center justify-center rounded-full",
              "border border-white/20 bg-black/45 text-white/90",
              "touch-none backdrop-blur-sm select-none [-webkit-touch-callout:none]",
              "disabled:pointer-events-none disabled:opacity-60",
              cameraViewActive
                ? "right-[max(0.75rem,env(safe-area-inset-right))]"
                : "top-[max(0.75rem,env(safe-area-inset-top))] left-1/2 -translate-x-1/2",
            )}
            style={
              cameraViewActive
                ? {
                    bottom: `calc(max(0.75rem, env(safe-area-inset-bottom)) + ${DEVICE_FLY_STICK_SIZE_PX}px + 0.75rem)`,
                  }
                : undefined
            }
            title={t("locationsPage.scene3d.trajectory.goToPathStartHint")}
            aria-label={t("locationsPage.scene3d.trajectory.goToPathStartHint")}
            disabled={!hasCameraPath(cameraPath)}
            onContextMenu={(e) => e.preventDefault()}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => {
              const engine = engineRef.current
              if (!engine) return
              engine.placeAtPathStart()
              deviceLookRef.current?.calibrate(engine.getFlyLook())
              if (cameraViewActive) setViewPlaybackEnded(false)
            }}
          >
            <Crosshair className="h-3.5 w-3.5" />
          </button>
          {canEdit ? (
            <PhoneFlyRecordButton
              bottomClass={
                cameraViewActive ? "bottom-[max(0.75rem,env(safe-area-inset-bottom))]" : DEVICE_FLY_STICK_BOTTOM_CLASS
              }
              recording={recording}
              recordArmed={recordArmed}
              label={recordButtonLabel}
              className={cn(
                !gyroReady && "pointer-events-none",
                cameraViewActive && "right-[max(0.75rem,env(safe-area-inset-right))]",
              )}
              onToggle={cameraViewActive ? togglePhoneLookRewrite : togglePhoneFlyRecord}
            />
          ) : null}
          <button
            type="button"
            className={cn(
              "pointer-events-auto absolute top-[max(0.75rem,env(safe-area-inset-top))] right-[max(0.75rem,env(safe-area-inset-right))] z-[80]",
              "flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-black/45 text-white/90",
              "touch-none backdrop-blur-sm select-none [-webkit-touch-callout:none]",
            )}
            aria-label={t("locationsPage.scene3d.deviceCamera.disable")}
            onContextMenu={(e) => e.preventDefault()}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => stopDeviceCamera()}
          >
            <X className="h-3.5 w-3.5" />
          </button>
          {cameraViewActive ? (
            <button
              type="button"
              className={cn(
                "pointer-events-auto absolute z-50 flex items-center justify-center rounded-lg",
                "border border-white/25 bg-black/45 text-white/90 shadow-lg",
                "touch-none backdrop-blur-sm outline-none select-none [-webkit-touch-callout:none]",
                "focus:outline-none focus-visible:ring-0 focus-visible:outline-none",
                "disabled:pointer-events-none disabled:opacity-60",
                !gyroReady && "pointer-events-none",
                "bottom-[max(0.75rem,env(safe-area-inset-bottom))]",
                "left-[max(0.75rem,env(safe-area-inset-left))]",
              )}
              style={{ width: DEVICE_FLY_STICK_SIZE_PX, height: DEVICE_FLY_STICK_SIZE_PX }}
              title={
                playing
                  ? t("locationsPage.scene3d.stopPlay")
                  : viewPlaybackEnded
                    ? t("locationsPage.scene3d.trajectory.goToPathStartHint")
                    : t("locationsPage.scene3d.play")
              }
              aria-label={
                playing
                  ? t("locationsPage.scene3d.stopPlay")
                  : viewPlaybackEnded
                    ? t("locationsPage.scene3d.trajectory.goToPathStartHint")
                    : t("locationsPage.scene3d.play")
              }
              disabled={!hasCameraPath(cameraPath)}
              onContextMenu={(e) => e.preventDefault()}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => {
                const engine = engineRef.current
                if (!engine) return
                if (engine.isPlaying()) {
                  engine.placeAtPathStart()
                  setViewPlaybackEnded(false)
                  return
                }
                if (viewPlaybackEnded) {
                  engine.placeAtPathStart()
                  setViewPlaybackEnded(false)
                  return
                }
                engine.togglePathPlayback()
              }}
            >
              {playing ? (
                <Square className="h-7 w-7" strokeWidth={1} aria-hidden />
              ) : viewPlaybackEnded ? (
                <SkipBack className="h-7 w-7" strokeWidth={1} aria-hidden />
              ) : (
                <Play className="h-7 w-7" strokeWidth={1} aria-hidden />
              )}
            </button>
          ) : null}
          {!gyroReady ? (
            <button
              type="button"
              className={cn(
                "pointer-events-auto absolute inset-0 z-[70] flex flex-col items-center justify-start bg-black/45 px-6 pt-[25%]",
                "touch-manipulation select-none [-webkit-touch-callout:none]",
              )}
              aria-label={t("locationsPage.scene3d.deviceCamera.tapToLook")}
              onContextMenu={(e) => e.preventDefault()}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                ensureDeviceOrientationLook()
              }}
            >
              <span className="text-center text-sm font-medium text-white/95">
                {t("locationsPage.scene3d.deviceCamera.tapToLook")}
              </span>
            </button>
          ) : null}
          {cameraViewActive ? null : (
            <>
              <DeviceFlyStick
                side="left"
                aria-label={t("locationsPage.scene3d.deviceCamera.leftStick")}
                className={cn("absolute z-50", DEVICE_FLY_STICK_BOTTOM_CLASS)}
                onPress={onLeftStickPress}
                onMove={onLeftStickMove}
                onRelease={onLeftStickRelease}
              />
              <DeviceFlyStick
                side="right"
                aria-label={t("locationsPage.scene3d.deviceCamera.rightStick")}
                className="absolute top-1/2 z-50 -translate-y-1/2"
                onPress={onRightStickPress}
                onMove={onRightStickMove}
                onRelease={onRightStickRelease}
              />
            </>
          )}
        </>
      ) : null}
      {phoneLayout && !deviceCameraActive && canEdit ? (
        <>
          <PhoneFlyRecordButton
            bottomClass={DEVICE_FLY_STICK_MODAL_BOTTOM_CLASS}
            recording={recording}
            recordArmed={recordArmed}
            label={recordButtonLabel}
            onToggle={togglePhoneFlyRecord}
          />
          <DeviceFlyStick
            side="left"
            aria-label={t("locationsPage.scene3d.deviceCamera.leftStick")}
            className={cn("absolute z-50", DEVICE_FLY_STICK_MODAL_BOTTOM_CLASS)}
            onPress={onLeftStickPress}
            onMove={onLeftStickMove}
            onRelease={onLeftStickRelease}
          />
          <DeviceFlyStick
            side="right"
            aria-label={t("locationsPage.scene3d.deviceCamera.rightStick")}
            className="absolute top-1/2 z-50 -translate-y-1/2"
            onPress={onRightStickPress}
            onMove={onRightStickMove}
            onRelease={onRightStickRelease}
          />
        </>
      ) : null}
      {!deviceCameraActive && gizmoAnchor && selectedUids.length === 1 ? (
        <StageAnchored
          anchorBox={{ x: gizmoAnchor.x - 28, y: gizmoAnchor.y }}
          prefer="up"
          align="center"
          gap={6}
          className="z-40 flex flex-col gap-0.5 rounded-lg bg-black/55 p-0.5 [&_button]:focus-visible:ring-0"
        >
          {(
            [
              ["rotate", Rotate3d, t("locationsPage.scene3d.gizmo.rotate")],
              ["scale", Scaling, t("locationsPage.scene3d.gizmo.scale")],
            ] as const
          ).map(([mode, Icon, label]) => (
            <EditToolbarButton
              key={mode}
              title={label}
              active={gizmoMode === mode}
              className={objectPopoverButtonClass(gizmoMode === mode)}
              onClick={() => {
                if (pathEdit.objectPathUid != null) engineRef.current?.setObjectPathEdit(null)
                setHeroFigureOpen(false)
                closeObjectColorPopover()
                setSceneGizmoMode(gizmoMode === mode ? "translate" : mode)
              }}
            >
              <Icon className="h-4 w-4" />
            </EditToolbarButton>
          ))}
          {!phoneLayout && selectedUid ? (
            <EditToolbarButton
              title={t("locationsPage.scene3d.objectPath")}
              active={pathEdit.objectPathUid === selectedUid}
              className={objectPopoverButtonClass(pathEdit.objectPathUid === selectedUid)}
              onClick={() => {
                const entering = pathEdit.objectPathUid !== selectedUid
                if (entering) {
                  setSceneGizmoMode("translate")
                  setHeroFigureOpen(false)
                  closeObjectColorPopover()
                }
                engineRef.current?.setObjectPathEdit(selectedUid)
              }}
            >
              <ObjectTrajectoryIcon className="h-4 w-4" />
            </EditToolbarButton>
          ) : null}
          {objects.find((item) => item.uid === selectedUid)?.kind === "character" ? (
            <div ref={heroFigurePopoverRef}>
              <EditToolbarButton
                title={t("locationsPage.scene3d.heroFigure")}
                className={objectPopoverButtonClass(heroFigureOpen)}
                active={heroFigureOpen}
                onClick={() => {
                  if (heroFigureOpen) {
                    setHeroFigureOpen(false)
                    return
                  }
                  closeObjectColorPopover()
                  if (pathEdit.objectPathUid != null) engineRef.current?.setObjectPathEdit(null)
                  setSceneGizmoMode("translate")
                  setHeroFigureOpen(true)
                }}
              >
                <Shapes className="h-4 w-4" />
              </EditToolbarButton>
              <StageAnchored
                open={heroFigureOpen}
                anchorRef={heroFigurePopoverRef}
                prefer="right"
                panelRef={heroFigurePanelRef}
                className="z-50 w-max"
              >
                <HeroFigurePicker
                  value={characterFigureOrHumanoid(objects.find((item) => item.uid === selectedUid)?.figure)}
                  labelFor={heroFigureLabel}
                  onPick={(figure) => {
                    if (selectedUid == null) return
                    engineRef.current?.setObjectFigure(selectedUid, figure)
                    setHeroFigureOpen(false)
                  }}
                />
              </StageAnchored>
            </div>
          ) : null}
          <div ref={objectColorPopoverRef}>
            <EditToolbarButton
              title={t("locationsPage.scene3d.objectColor")}
              className={objectPopoverButtonClass(objectColorOpen)}
              active={objectColorOpen}
              onClick={() => {
                if (objectColorOpen) closeObjectColorPopover()
                else {
                  setHeroFigureOpen(false)
                  if (pathEdit.objectPathUid != null) engineRef.current?.setObjectPathEdit(null)
                  setSceneGizmoMode("translate")
                  setObjectColorOpen(true)
                }
              }}
            >
              <Palette className="h-4 w-4" />
            </EditToolbarButton>
            <StageAnchored
              open={objectColorOpen}
              anchorRef={objectColorPopoverRef}
              prefer="right"
              panelRef={objectColorPanelRef}
              className="z-50 w-max"
            >
              <SceneColorPicker
                value={objectColor}
                onChange={previewObjectColor}
                onPreset={(hex) => {
                  previewObjectColor(hex)
                  objectColorDirtyRef.current = false
                  setObjectColorOpen(false)
                  if (selectedUid != null) engineRef.current?.setObjectColor(selectedUid, hex, { emit: true })
                }}
                onClear={() => {
                  objectColorDirtyRef.current = false
                  objectColorPendingHexRef.current = null
                  setObjectColor(objectColorDefault)
                  setObjectColorOpen(false)
                  if (selectedUid != null) {
                    engineRef.current?.setObjectColor(selectedUid, objectColorDefault, { emit: true })
                  }
                }}
                labels={{
                  color: t("locationsPage.scene3d.objectColor"),
                  presetGreen: t("locationsPage.scenePlatform.wallColorPresetGreen"),
                  clearColor: t("locationsPage.scene3d.objectColorClear"),
                }}
              />
            </StageAnchored>
          </div>
          <EditToolbarButton
            title={t("locationsPage.scene3d.removeObject")}
            className="h-8 w-8 text-white/85 hover:bg-white/20 hover:text-white"
            onClick={() => {
              if (selectedUid == null) return
              engineRef.current?.removeObject(selectedUid)
            }}
          >
            <Trash2 className="h-4 w-4" />
          </EditToolbarButton>
        </StageAnchored>
      ) : null}
      {!deviceCameraActive && gizmoAnchor && groupAction ? (
        <StageAnchored
          anchorBox={{ x: gizmoAnchor.x - 28, y: gizmoAnchor.y }}
          prefer="up"
          align="center"
          gap={6}
          className="z-40 flex flex-col gap-0.5 rounded-lg bg-black/55 p-0.5 [&_button]:focus-visible:ring-0"
        >
          {groupAction === "ungroup" ? (
            <EditToolbarButton
              title={t("locationsPage.scene3d.gizmo.rotate")}
              active={gizmoMode === "rotate"}
              className={cn(
                "h-8 w-8 text-white/85 hover:bg-white/20 hover:text-white",
                gizmoMode === "rotate" && "bg-white/20 text-white ring-white/40",
              )}
              onClick={() => setSceneGizmoMode(gizmoMode === "rotate" ? "translate" : "rotate")}
            >
              <Rotate3d className="h-4 w-4" />
            </EditToolbarButton>
          ) : null}
          <EditToolbarButton
            title={t(
              groupAction === "group" ? "locationsPage.scene3d.groupObjects" : "locationsPage.scene3d.ungroupObjects",
            )}
            className="h-8 w-8 text-white/85 hover:bg-white/20 hover:text-white"
            onClick={() => {
              if (groupAction === "group") engineRef.current?.groupSelection()
              else engineRef.current?.ungroupSelection()
            }}
          >
            {groupAction === "group" ? <Group className="h-4 w-4" /> : <Ungroup className="h-4 w-4" />}
          </EditToolbarButton>
          {groupAction === "ungroup" ? (
            <EditToolbarButton
              title={t("locationsPage.scene3d.removeObject")}
              className="h-8 w-8 text-white/85 hover:bg-white/20 hover:text-white"
              onClick={() => {
                engineRef.current?.removeObjects(selectedUids.slice())
              }}
            >
              <Trash2 className="h-4 w-4" />
            </EditToolbarButton>
          ) : null}
        </StageAnchored>
      ) : null}
      {!deviceCameraActive && lightAnchor ? (
        <StageAnchored
          anchorBox={{ x: lightAnchor.x, y: lightAnchor.y }}
          prefer="up"
          align="center"
          gap={6}
          className="z-40 flex w-44 flex-col rounded-lg bg-black/55 px-2.5 py-2"
        >
          <UiRange
            className="w-full"
            min={lightIntensityRange.min}
            max={lightIntensityRange.max}
            step={0.05}
            value={lightIntensity}
            aria-label={t("locationsPage.scene3d.lightIntensity")}
            onPointerDown={() => {
              lightIntensityDraggingRef.current = true
            }}
            onPointerUp={() => {
              lightIntensityDraggingRef.current = false
              const eng = engineRef.current
              if (!eng) return
              eng.setLightIntensity(eng.getLightIntensity(), { emit: true })
            }}
            onPointerCancel={() => {
              lightIntensityDraggingRef.current = false
              const eng = engineRef.current
              if (!eng) return
              eng.setLightIntensity(eng.getLightIntensity(), { emit: true })
            }}
            onChange={(event) => {
              const next = Number(event.target.value)
              setLightIntensity(next)
              engineRef.current?.setLightIntensity(next, { emit: false })
              const prev = platformVisualRef.current
              if (prev?.light) {
                platformVisualRef.current = { ...prev, light: { ...prev.light, intensity: next } }
              }
            }}
          />
        </StageAnchored>
      ) : null}
      {!deviceCameraActive && selectionExtrudeUi?.visible && selectionExtrudeAnchor ? (
        <StageAnchored
          anchorBox={{ x: selectionExtrudeAnchor.x, y: selectionExtrudeAnchor.y }}
          prefer="up"
          align={selectionExtrudeAnchor.kind === "face" ? "start" : "center"}
          gap={6}
          className={cn(
            "z-40 flex gap-0.5 rounded-lg bg-black/55 p-0.5 [&_button]:focus-visible:ring-0",
            selectionExtrudeAnchor.kind === "face" ? "flex-row" : "flex-col",
          )}
        >
          {selectionExtrudeUi.kind === "face" ? (
            <>
              {selectionExtrudeAnchor.faceArrowFlip ? (
                <>
                  <EditToolbarButton
                    title={t("locationsPage.scenePlatform.shiftIn")}
                    className="h-8 w-8 text-white/85 hover:bg-white/20 hover:text-white disabled:opacity-40"
                    disabled={!selectionExtrudeUi.canIntrude}
                    onClick={() => selectionExtrudeRef.current?.intrude()}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </EditToolbarButton>
                  <EditToolbarButton
                    title={t("locationsPage.scenePlatform.shiftOut")}
                    className="h-8 w-8 text-white/85 hover:bg-white/20 hover:text-white disabled:opacity-40"
                    disabled={!selectionExtrudeUi.canExtrude}
                    onClick={() => selectionExtrudeRef.current?.extrude()}
                  >
                    <ArrowRight className="h-4 w-4" />
                  </EditToolbarButton>
                </>
              ) : (
                <>
                  <EditToolbarButton
                    title={t("locationsPage.scenePlatform.shiftOut")}
                    className="h-8 w-8 text-white/85 hover:bg-white/20 hover:text-white disabled:opacity-40"
                    disabled={!selectionExtrudeUi.canExtrude}
                    onClick={() => selectionExtrudeRef.current?.extrude()}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </EditToolbarButton>
                  <EditToolbarButton
                    title={t("locationsPage.scenePlatform.shiftIn")}
                    className="h-8 w-8 text-white/85 hover:bg-white/20 hover:text-white disabled:opacity-40"
                    disabled={!selectionExtrudeUi.canIntrude}
                    onClick={() => selectionExtrudeRef.current?.intrude()}
                  >
                    <ArrowRight className="h-4 w-4" />
                  </EditToolbarButton>
                </>
              )}
            </>
          ) : (
            <>
              <EditToolbarButton
                title={t("locationsPage.scenePlatform.extrude")}
                className="h-8 w-8 text-white/85 hover:bg-white/20 hover:text-white disabled:opacity-40"
                disabled={!selectionExtrudeUi.canExtrude}
                onClick={() => selectionExtrudeRef.current?.extrude()}
              >
                <ArrowUp className="h-4 w-4" />
              </EditToolbarButton>
              <EditToolbarButton
                title={t("locationsPage.scenePlatform.intrude")}
                className="h-8 w-8 text-white/85 hover:bg-white/20 hover:text-white disabled:opacity-40"
                disabled={!selectionExtrudeUi.canIntrude}
                onClick={() => selectionExtrudeRef.current?.intrude()}
              >
                <ArrowDown className="h-4 w-4" />
              </EditToolbarButton>
              <div ref={floorColorPopoverRef}>
                <EditToolbarButton
                  title={t("locationsPage.scenePlatform.floorColor")}
                  className="h-8 w-8 text-white/85 hover:bg-white/20 hover:text-white disabled:opacity-40"
                  active={floorColorOpen}
                  onClick={() => {
                    if (floorColorOpen) closeFloorColorPopover()
                    else setFloorColorOpen(true)
                  }}
                >
                  <Palette className="h-4 w-4" />
                </EditToolbarButton>
                <StageAnchored
                  open={floorColorOpen}
                  anchorRef={floorColorPopoverRef}
                  prefer="right"
                  panelRef={floorColorPanelRef}
                  className="z-50 w-max"
                >
                  <SceneColorPicker
                    value={floorColor}
                    onChange={previewFloorColor}
                    onPreset={(hex) => {
                      previewFloorColor(hex)
                      floorColorDirtyRef.current = false
                      setFloorColorOpen(false)
                      void selectionExtrudeRef.current?.commitSolidColor?.(hex)
                    }}
                    onClear={() => {
                      floorColorDirtyRef.current = false
                      setFloorColorOpen(false)
                      void selectionExtrudeRef.current?.clearSolidColor?.()
                    }}
                    labels={{
                      color: t("locationsPage.scenePlatform.floorColor"),
                      presetGreen: t("locationsPage.scenePlatform.wallColorPresetGreen"),
                      clearColor: t("locationsPage.scenePlatform.floorColorClear"),
                    }}
                  />
                </StageAnchored>
              </div>
            </>
          )}
        </StageAnchored>
      ) : null}
      {flashKey > 0 ? <div key={flashKey} className="scene-control-flash absolute inset-0 z-30" aria-hidden /> : null}
    </div>
  )

  const timeline = (
    <LocationScenePathTimeline
      engineRef={engineRef}
      cameraPath={cameraPath}
      playing={playing}
      recording={recording}
      lookRecording={pathEdit.lookRecording}
      canEdit={canEdit}
      clockMs={sceneClockMs(cameraPathDurationMs(cameraPath), objects)}
      motionReady={sceneHasMotion(cameraPath, objects)}
      objectPathEditing={pathEdit.objectPathUid != null}
      editDurationMs={pathEdit.objectPathUid != null ? pathEdit.durationMs : undefined}
      objectSpeedPath={
        selectedUid
          ? (objects.find((item) => item.uid === selectedUid && objectPathHasContent(item.path))?.path ?? null)
          : null
      }
      objectSpeedUid={selectedUid}
      projectId={projectId}
      locationId={locationId}
      previzName={sceneName}
      getSceneStateForPreviz={getSceneStateForPreviz}
      shotId={shotId}
      scene3dId={boundScene3dId ?? scene3dId}
      onPrevizSaved={handlePrevizExported}
      onPrevizCreateStart={onPrevizCreateStart}
      onPrevizSaveFailed={onPrevizSaveFailed}
      dismissAfterPrevizSave={dismissAfterPrevizSave}
      onDismissAfterPrevizCapture={onDismissAfterPrevizCapture}
      onExportingChange={handleExportingChange}
      onCapturingChange={handleCapturingChange}
      pathTimeListenerRef={pathTimeListenerRef}
      bindSaveRef={overlaySaveRef}
    />
  )

  const themeIsLight = environment.theme === "light"
  const themeSwitchLabel = t(
    themeIsLight ? "locationsPage.scene3d.environment.themeToNight" : "locationsPage.scene3d.environment.themeToDay",
  )

  const modeSaveBaseline = modeSaveBaselineRef.current
  const modeSaveObjectPath =
    pathEdit.objectPathUid != null
      ? objectPathFingerprint(objects.find((item) => item.uid === pathEdit.objectPathUid)?.path)
      : null
  const modeSaveDirty =
    modeSaveBaseline != null &&
    modeSaveBaseline.key === modeSaveKey &&
    (modeSaveKey === "place"
      ? !cameraPathsEqual(modeSaveBaseline.cameraPath, cameraPath) ||
        !startCamerasEqual(modeSaveBaseline.start, startCameraRef.current)
      : modeSaveKey === "camera"
        ? !cameraPathsEqual(modeSaveBaseline.cameraPath, cameraPath)
        : modeSaveObjectPath !== modeSaveBaseline.objectPath)

  const sceneControls = showSceneControls ? (
    <>
      <div className="pointer-events-none absolute bottom-4 left-4 z-30">
        <div className="pointer-events-auto">
          <MoveKeys
            skin={environment.theme}
            hints={moveKeyHints}
            onPress={onMoveKeyPress}
            onRelease={onMoveKeyRelease}
          />
        </div>
      </div>
      {canEdit ? (
        <div className="pointer-events-none absolute right-4 bottom-4 z-30">
          <div className="pointer-events-auto flex flex-col items-end gap-2.5">
            {showPlaceMonitor ? (
              <canvas
                ref={placeMonitorRef}
                className="pointer-events-none block w-[200px] overflow-hidden rounded-none"
                style={{ aspectRatio: frameFormatRatio(frame) }}
              />
            ) : null}
            <CaptureControls
              skin={environment.theme}
              phase={capturePhase}
              elapsedMs={overlayElapsedMs}
              snapshotLabel={t("locationsPage.scene3d.captureViewpoint")}
              recordLabel={overlayRecordLabel}
              snapshotDisabled={photoBusy}
              recordDisabled={playing}
              snapshotHint={
                captureHintStep === "snapshot" ? t("locationsPage.scene3d.captureHintViewpoint") : undefined
              }
              recordHint={captureHintStep === "record" ? t("locationsPage.scene3d.captureHintFlight") : undefined}
              onSnapshot={onOverlaySnapshot}
              onRecord={onOverlayRecord}
            />
          </div>
        </div>
      ) : null}
    </>
  ) : null

  return (
    <div className="relative flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
      {!deviceCameraActive && !phoneBooting ? (
        <div className={cn("shrink-0", previzExporting && "invisible")}>
          <div className={cn("scene-toolbar", previzExporting && "pointer-events-none")}>{toolbar}</div>
          {previzExporting ? null : addPicker}
        </div>
      ) : null}
      <div ref={placeholderRef} className="relative min-h-0 flex-1 overflow-hidden bg-[#d0d1d2]">
        {stageSurface}
        {!deviceCameraActive && !previzExporting && !phoneBooting && (canEdit || liveSyncBadge !== "idle") ? (
          <div className="pointer-events-none absolute top-2.5 left-[14px] z-30 flex flex-col items-start gap-1">
            {liveSyncBadge !== "idle" ? (
              <div
                className={cn(
                  "pointer-events-none rounded-md px-2 py-0.5 text-[11px] font-medium tracking-wide text-white/95",
                  liveSyncBadge === "live" ? "bg-red-600/80" : "bg-ui-accent/80",
                )}
              >
                {liveSyncBadge === "live"
                  ? t("locationsPage.scene3d.liveSync.live")
                  : t("locationsPage.scene3d.liveSync.following")}
              </div>
            ) : null}
            {canEdit ? (
              <>
                <ActionHintTooltip
                  label={t("locationsPage.scene3d.environment.themeSwitch")}
                  className="pointer-events-auto h-auto"
                >
                  <button
                    type="button"
                    aria-label={themeSwitchLabel}
                    className={cn(
                      "pointer-events-auto flex size-[26px] cursor-pointer items-center justify-center rounded-sm select-none",
                      "border-0 bg-transparent p-0 transition-colors duration-[0.12s] outline-none",
                      themeIsLight
                        ? "text-[var(--text-muted)] hover:text-[var(--scene-chrome-hover-day)]"
                        : "text-[var(--scene-cube-night-icon)] hover:text-[var(--scene-chrome-hover-night)]",
                    )}
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation()
                      const next: SceneThemeId = themeIsLight ? "dark" : "light"
                      const environmentNext = { ...environment, theme: next }
                      setEnvironment(environmentNext)
                      engineRef.current?.applyEnvironment(environmentNext)
                      setPlatformLayout((prev) =>
                        prev ? { ...prev, environment: environmentNext, exists: true } : prev,
                      )
                      queuePersist()
                    }}
                  >
                    {themeIsLight ? (
                      <Sun
                        className="size-5"
                        strokeWidth={1.3}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      />
                    ) : (
                      <Moon
                        className="size-5"
                        strokeWidth={1.3}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      />
                    )}
                  </button>
                </ActionHintTooltip>
                {canLookFromCamera(cameraPath) ? (
                  <ActionHintTooltip
                    label={t(
                      pathEdit.lookThrough
                        ? "locationsPage.scene3d.trajectory.freeView"
                        : "locationsPage.scene3d.trajectory.lookThrough",
                    )}
                    className="pointer-events-auto h-auto"
                  >
                    <button
                      type="button"
                      aria-label={t(
                        pathEdit.lookThrough
                          ? "locationsPage.scene3d.trajectory.freeView"
                          : "locationsPage.scene3d.trajectory.lookThrough",
                      )}
                      aria-pressed={pathEdit.lookThrough}
                      className={cn(
                        "pointer-events-auto flex size-[26px] cursor-pointer items-center justify-center rounded-sm select-none",
                        "border-0 bg-transparent p-0 transition-colors duration-[0.12s] outline-none",
                        themeIsLight
                          ? "text-[var(--text-muted)] hover:text-[var(--scene-chrome-hover-day)]"
                          : "text-[var(--scene-cube-night-icon)] hover:text-[var(--scene-chrome-hover-night)]",
                      )}
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => {
                        event.stopPropagation()
                        engineRef.current?.setPathLookThrough(!pathEdit.lookThrough)
                      }}
                    >
                      {pathEdit.lookThrough ? (
                        <Box
                          className="size-5"
                          strokeWidth={1.3}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        />
                      ) : (
                        <Eye
                          className="size-5"
                          strokeWidth={1.3}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        />
                      )}
                    </button>
                  </ActionHintTooltip>
                ) : null}
                {hasCameraPath(cameraPath) && phoneLayout ? (
                  <ActionHintTooltip label={viewQrLabel} className="pointer-events-auto h-auto">
                    <button
                      type="button"
                      aria-label={viewQrLabel}
                      className={cn(
                        "pointer-events-auto flex size-[26px] cursor-pointer items-center justify-center rounded-sm select-none",
                        "border-0 bg-transparent p-0 transition-colors duration-[0.12s] outline-none",
                        themeIsLight
                          ? "text-[var(--text-muted)] hover:text-[var(--scene-chrome-hover-day)]"
                          : "text-[var(--scene-cube-night-icon)] hover:text-[var(--scene-chrome-hover-night)]",
                      )}
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => {
                        event.stopPropagation()
                        enterDeviceCameraUi("view")
                      }}
                    >
                      <MonitorSmartphone
                        className="size-5"
                        strokeWidth={1.3}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      />
                    </button>
                  </ActionHintTooltip>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}
        {!deviceCameraActive && !previzExporting && !phoneBooting ? (
          <div className={VIEW_CUBE_CORNER_CLASS}>
            <div className="pointer-events-auto">
              <ViewCubeWidget
                ref={viewCubeRef}
                labels={viewCubeLabels}
                isoLabel={t("locationsPage.scene3d.viewPreset.iso")}
                theme={environment.theme}
                showCube={!phoneLayout}
                onViewSelect={(viewId) => {
                  engineRef.current?.animateToView(viewId)
                  queuePersist()
                }}
                onOrbitStart={() => {
                  viewCubeDragRef.current = true
                }}
                onOrbit={(dx, dy) => {
                  engineRef.current?.orbitAroundFocus(dx, dy)
                }}
                onOrbitEnd={() => {
                  viewCubeDragRef.current = false
                  queuePersist()
                }}
              />
            </div>
          </div>
        ) : null}
        {modeSaveKey && !deviceCameraActive && !previzExporting && !phoneBooting ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-4 z-30 flex justify-center">
            <Button
              type="button"
              size="sm"
              className="pointer-events-auto"
              disabled={!modeSaveDirty}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={() => {
                const engine = engineRef.current
                if (!engine || !modeSaveDirty) return
                if (cameraPlaceMode) {
                  engine.setCameraPlaceMode(false)
                  return
                }
                if (pathEdit.objectPathUid) {
                  engine.setObjectPathEdit(null)
                  engine.selectObject(null)
                  return
                }
                engine.setTrajectoryMode(false)
              }}
            >
              {t("locationsPage.scene3d.modeSave")}
            </Button>
          </div>
        ) : null}
        {sceneControls}
      </div>
      {!deviceCameraActive ? (
        <div className={cn("shrink-0", previzExporting && "invisible")}>
          <div className={cn(previzExporting && "pointer-events-none")}>{timeline}</div>
        </div>
      ) : null}
      <ConfirmActionDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        tone="danger"
        title={t("locationsPage.scene3d.reset")}
        message={t("locationsPage.scene3d.resetConfirm")}
        onConfirm={() => void handleReset()}
      />
      <ConfirmActionDialog
        open={overwriteRecordOpen}
        onOpenChange={setOverwriteRecordOpen}
        tone="danger"
        title={t("locationsPage.scene3d.timeline.overwriteTake")}
        message={t("locationsPage.scene3d.timeline.overwriteTakeConfirm")}
        onConfirm={() => engineRef.current?.startRecording({ overwrite: true })}
      />
      <ConfirmActionDialog
        open={cameraPlaceConfirmOpen}
        onOpenChange={setCameraPlaceConfirmOpen}
        title={t("locationsPage.scene3d.placeCameraNewTakeTitle")}
        message={t("locationsPage.scene3d.placeCameraNewTakeMessage")}
        onConfirm={confirmCameraPlaceTake}
      />
      <SketchDialog
        open={pickerPreviewRef != null}
        onOpenChange={(open) => {
          if (!open) setPickerPreviewRef(null)
        }}
        projectId={projectId}
        sourceUrl={
          pickerPreviewRef != null && pickerPreviewRef.media_type !== "video"
            ? (pickerPreviewRef.download_url ?? pickerPreviewRef.url)
            : null
        }
        videoUrl={
          pickerPreviewRef != null && pickerPreviewRef.media_type === "video"
            ? (pickerPreviewRef.download_url ?? pickerPreviewRef.url)
            : null
        }
        presentation="view"
        allowEdit={false}
        persist="none"
        headerTitle={pickerPreviewRef?.label ?? null}
        initialFileName={pickerPreviewRef?.label ?? null}
        nested={2}
      />
      {phoneBooting ? (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black">
          <button
            type="button"
            className={cn(
              "pointer-events-auto absolute top-[max(0.75rem,env(safe-area-inset-top))]",
              "right-[max(0.75rem,env(safe-area-inset-right))] z-50",
              "flex h-8 w-8 items-center justify-center rounded-full",
              "border border-white/20 bg-black/45 text-white/90",
            )}
            aria-label={t("locationsPage.scene3d.deviceCamera.disable")}
            onClick={() => onDeviceCameraExit?.()}
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <Loader2 className="h-6 w-6 animate-spin text-white/80" />
        </div>
      ) : null}
    </div>
  )
})
